import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import DataTableBase from "../interfaccia/DataTableBase";
import { BlockSkeleton, Skeleton } from "../interfaccia/loading";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const themeButtonClasses = {
  dark: "pointer bn br2 ph3 pv2 sfondoColScuro colBianco",
  primary: "pointer bn br2 ph3 pv2 tastoVerde white",
  warning: "pointer bn br2 ph3 pv2 tastoArancio white",
  danger: "pointer bn br2 ph3 pv2 tastoRosso white",
  neutral: "pointer bn br2 ph3 pv2 sfondoColChiaro scrittaDef",
  medium: "pointer bn br2 ph3 pv2 sfondoColMedio white",
  alt: "pointer bn br2 ph3 pv2 sfondoColAlt scrittaColAltScuro",
};

const themeSmallButtonClasses = {
  dark: "pointer bn br2 ph2 pv1 sfondoColScuro colBianco",
  primary: "pointer bn br2 ph2 pv1 tastoVerde white",
  warning: "pointer bn br2 ph2 pv1 tastoArancio white",
  danger: "pointer bn br2 ph2 pv1 tastoRosso white",
  neutral: "pointer bn br2 ph2 pv1 sfondoColChiaro scrittaDef",
};

const themePillClasses = {
  primary: "dib br-pill tastoVerde white ph2 pv1 mr2 mb2 f7",
  neutral: "dib br-pill sfondoColChiaro scrittaDef ph2 pv1 mr2 mb2 f7",
  info: "dib br-pill sfondoColChiaro scrittaColScuro ph2 pv1 mr2 mb2 f7",
};

// TODO(match-bottero-theme): verificare e riallineare le variabili del tema
// usate da questa pagina. Alcuni colori risultano ancora poco coerenti con il
// tema selezionato e il problema potrebbe essere nella configurazione delle
// variabili CSS del tema stesso, non solo nelle classi applicate qui.

const defaultFilters = {
  search: "",
  bsArticolo: "",
  bsGruppo1: "",
  bsGruppo2: "",
  bsGruppo3: "",
  vsArticolo: "",
  vsGruppo1: "",
  vsGruppo2: "",
  vsGruppo3: "",
  vsGruppo4: "",
  bsCodModello: "",
  vsCodModello: "",
  bsCodColore: "",
  vsCodColore: "",
  vsCodProduttore: "",
  bsBrand: "",
  bsBrandConvertito: "",
  vsBrand: "",
  bsVariante: "",
  bsStagione: "",
  vsVariante: "",
  bsTaglia: "",
  vsTaglia: "",
  bsNome: "",
  tagDaAggiungere: "",
  tagDaAggiungereEscluso: "",
  vsNome: "",
  ok: "0",
  conflittiArtVarVs: "",
  tipoMatchArtVar: "",
  quasiMatchSenzaTaglia: "",
  gestioneParziale: "",
  variantiCompletamenteIgnorate: "",
  eanAuditMode: "",
  eanHistoricCancelledBs: "",
};

const formatOkFilterLabel = (value) => {
  const normalized = String(value ?? "").trim();
  if (normalized === "1") return "Solo OK";
  if (normalized === "0") return "Solo non OK";
  return "Tutti";
};

const formatBrandSummaryVariantCountLabel = (okValue) => {
  const normalized = String(okValue ?? "").trim();
  if (normalized === "1") return "Articoli OK";
  if (normalized === "0") return "Articoli non OK";
  return "Articoli";
};

const EAN_AUDIT_MODE_OPTIONS = [
  { value: "", label: "Audit EAN: nessun filtro" },
  { value: "DIFF", label: "Audit EAN: solo differenze" },
  {
    value: "CREATE_UNIQUE",
    label: "Audit EAN: in creazione ma oggi match univoco",
  },
  {
    value: "IGNORE_UNIQUE",
    label: "Audit EAN: ignorati ma oggi match univoco",
  },
  {
    value: "MATCH_DIFFERS",
    label: "Audit EAN: match corrente diverso dalla simulazione",
  },
  {
    value: "AMBIGUOUS",
    label: "Audit EAN: simulazione ambigua",
  },
];

const EAN_HISTORIC_CANCELLED_BS_OPTIONS = [
  { value: "", label: "EAN storico con barcode BS annullato: nessun filtro" },
  { value: "1", label: "EAN storico con barcode BS annullato: solo interessati" },
  { value: "0", label: "EAN storico con barcode BS annullato: solo altri casi" },
];

const VARIANTI_COMPLETAMENTE_IGNORATE_OPTIONS = [
  { value: "", label: "Varianti completamente ignorate: nessun filtro" },
  { value: "1", label: "Varianti completamente ignorate: solo interessate" },
  { value: "0", label: "Varianti completamente ignorate: solo altri casi" },
];

const formatEanAuditModeLabel = (value) => {
  const option = EAN_AUDIT_MODE_OPTIONS.find((item) => item.value === value);
  return option ? option.label : "";
};

const formatEanHistoricCancelledBsLabel = (value) => {
  const option = EAN_HISTORIC_CANCELLED_BS_OPTIONS.find(
    (item) => item.value === value,
  );
  return option ? option.label : "";
};

const formatVariantiCompletamenteIgnorateLabel = (value) => {
  const option = VARIANTI_COMPLETAMENTE_IGNORATE_OPTIONS.find(
    (item) => item.value === value,
  );
  return option ? option.label : "";
};

const formatEanAuditDiffTypeLabel = (value) => {
  switch (String(value || "").trim()) {
    case "CREATE_BUT_SIMULATED_UNIQUE_EAN":
      return "In creazione, ma oggi avrebbe match EAN univoco";
    case "IGNORE_BUT_SIMULATED_UNIQUE_EAN":
      return "Ignorata, ma oggi avrebbe match EAN univoco";
    case "RIFARE_BUT_SIMULATED_UNIQUE_EAN":
      return "Articolo da rifare, ma oggi avrebbe match EAN univoco";
    case "TAGLIA_DA_AGGIUNGERE_BUT_SIMULATED_UNIQUE_EAN":
      return "Taglia da aggiungere, ma oggi avrebbe match EAN univoco";
    case "MATCH_DIFFERS_FROM_SIMULATED_EAN":
      return "Match corrente diverso dalla simulazione EAN";
    case "MATCH_ARTVAR_ONLY_BUT_SIMULATED_UNIQUE_EAN":
      return "Art-var assegnato senza taglia, ma oggi EAN sarebbe univoco";
    case "UNDECIDED_BUT_SIMULATED_UNIQUE_EAN":
      return "Da gestire, ma oggi EAN sarebbe univoco";
    case "SIMULATED_EAN_AMBIGUOUS":
      return "Simulazione EAN ambigua";
    case "CURRENT_MATCH_EQUALS_SIMULATED_EAN":
      return "Match corrente coerente con la simulazione EAN";
    default:
      return String(value || "").trim() || "-";
  }
};

const formatEanAuditCurrentDecisionLabel = (value) => {
  switch (String(value || "").trim()) {
    case "MATCH_EAN":
      return "Match EAN";
    case "MATCH_CODPROD":
      return "Match codice produttore";
    case "MATCH_MANUALE":
      return "Match manuale";
    case "MATCH_ALTRO":
      return "Match";
    case "MATCH_ARTVAR_SENZA_TAGLIA":
      return "Art-var senza taglia";
    case "CREATE":
      return "In creazione";
    case "IGNORE":
      return "Ignorata";
    case "ARTICOLO_DA_RIFARE":
      return "Articolo da rifare";
    case "TAGLIA_DA_AGGIUNGERE":
      return "Taglia da aggiungere";
    case "UNDECIDED":
      return "Da gestire";
    default:
      return String(value || "").trim() || "-";
  }
};

const buildEanAuditSimulatedTargetLabel = (row) => {
  if (!row || Number(row.simulatedEanCandidateCount || 0) !== 1) return "-";
  return [
    row.simulatedEanVsArticolo || "-",
    row.simulatedEanVsVariante || "-",
    row.simulatedEanVsTaglia || "-",
  ].join(" / ");
};

const formatEanAuditAmbiguityReasonLabel = (value) => {
  switch (String(value || "").trim()) {
    case "multiple_bs_barcodes":
      return "La taglia BS ha piu barcode fornitore";
    case "single_barcode_points_to_multiple_vs_targets":
      return "Almeno un barcode BS punta a piu target VS";
    case "multiple_barcodes_point_to_different_vs_targets":
      return "Barcode BS diversi puntano a target VS diversi";
    default:
      return String(value || "").trim() || "-";
  }
};

const buildEanAuditBarcodeSummary = (row) => {
  const diagnostics = Array.isArray(row?.simulatedEanBarcodeDiagnostics)
    ? row.simulatedEanBarcodeDiagnostics
    : [];
  if (!diagnostics.length) {
    return String(row?.simulatedEanMatchedBarcodeList || "").trim() || "-";
  }
  const reasons = Array.isArray(row?.simulatedEanAmbiguityReasons)
    ? row.simulatedEanAmbiguityReasons
    : [];
  if (!reasons.length) {
    return diagnostics.length > 1
      ? `${diagnostics.length} barcode BS coerenti`
      : "1 barcode BS coerente";
  }
  return reasons.map(formatEanAuditAmbiguityReasonLabel).join("; ");
};

const extractVsCodesFromTargetLabels = (labels) =>
  Array.from(
    new Set(
      (Array.isArray(labels) ? labels : [])
        .map((label) => String(label || "").split(" / ")[0].trim())
        .filter(Boolean),
    ),
  );

const buildEanAuditVsCodesSummary = (row) => {
  const diagnostics = Array.isArray(row?.simulatedEanBarcodeDiagnostics)
    ? row.simulatedEanBarcodeDiagnostics
    : [];
  const codes = Array.from(
    new Set(
      diagnostics.flatMap((item) => extractVsCodesFromTargetLabels(item.vsTargetLabels)),
    ),
  );
  if (codes.length) return codes.join(", ");
  return row?.simulatedEanVsArticolo || row?.vsArticolo || "-";
};

const normalizeBarcodeDetails = (details, fallbackList) => {
  const rows = Array.isArray(details)
    ? details
        .map((item) => ({
          barcode: String(item?.barcode || "").trim(),
          annullato: Number(item?.annullato ?? 0),
        }))
        .filter((item) => item.barcode)
    : [];
  if (rows.length) return rows;
  return String(fallbackList || "")
    .split(",")
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((barcode) => ({ barcode, annullato: 0 }));
};

const normalizeSortDirection = (value) =>
  value === "desc" || value === "DESC" ? "desc" : "asc";
const parseTagList = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    );
  }
  const text = String(value || "").trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return Array.from(
        new Set(
          parsed
            .map((item) => String(item || "").trim())
            .filter(Boolean),
        ),
      );
    }
  } catch (_error) {}
  return Array.from(
    new Set(
      text
        .split(",")
        .map((item) => String(item || "").trim())
        .filter(Boolean),
    ),
  );
};
const formatTagOptionLabel = (option) => {
  const code = String(option?.code || "").trim();
  const description = String(option?.description || "").trim();
  if (!code) return "";
  return description ? `${code} - ${description}` : code;
};

const extractCodColFromBsVariante = (value) => {
  const text = String(value || "").trim();
  if (!text.includes("|")) return "";
  const rawCode = text.split("|")[0];
  const code = String(rawCode || "").trim();
  return code || "";
};
const extractColorDescriptionFromBsVariante = (value) => {
  const text = String(value || "").trim();
  if (!text.includes("|")) return text;
  const [, ...parts] = text.split("|");
  return String(parts.join("|") || "").trim();
};
const buildColorConsonantsSuffix = (value) => {
  const description = extractColorDescriptionFromBsVariante(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  const consonants = Array.from(description)
    .filter((char) => /[BCDFGHJKLMNPQRSTVWXYZ]/.test(char))
    .join("");
  return consonants ? `-${consonants}` : "";
};
const leftCreateValue = (value, maxLength) =>
  String(value || "").slice(0, Math.max(0, Number(maxLength) || 0));
const buildCsvContent = (columns, rows) => {
  const header = columns.join(";");
  const body = rows
    .map((row) =>
      columns
        .map((key) => {
          const raw = row[key] ?? "";
          const value = typeof raw === "string" ? raw : JSON.stringify(raw);
          const escaped = value.replace(/"/g, '""');
          if (/[";\n]/.test(escaped)) {
            return `"${escaped}"`;
          }
          return escaped;
        })
        .join(";"),
    )
    .join("\n");
  return `${header}\n${body}`;
};
const downloadCsvFile = (content, filename) => {
  const blob = new Blob([content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

function MatchBotteroProgressSkeleton() {
  return (
    <div className="flex justify-between">
      <div className="f7 w-20">
        <BlockSkeleton tone="soft" width="88%" height={12} />
      </div>
      <div className="w-70 db">
        <div className="flex justify-between mb2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`progress-kpi-${index}`}
              className={`${index === 1 ? "tc" : index > 1 ? "tr" : "tl"} w-25`}
            >
              <Skeleton
                tone="soft"
                inline
                width={40}
                height={20}
                className="mb1"
                style={{ display: "block", marginLeft: index > 0 ? "auto" : 0 }}
              />
              <Skeleton
                tone="soft"
                inline
                width={index === 2 ? 84 : 74}
                height={12}
                style={{ display: "block", marginLeft: index > 0 ? "auto" : 0 }}
              />
            </div>
          ))}
        </div>
        <BlockSkeleton
          tone="soft"
          width="100%"
          height={14}
          borderRadius={8}
        />
      </div>
    </div>
  );
}

function MatchBotteroBrandSelectionTableSkeleton() {
  return (
    <div className="overflow-auto mb2">
      <table
        className="collapse f7"
        style={{ tableLayout: "fixed", width: "600px" }}
      >
        <thead>
          <tr>
            <th
              className="tl pa2 bb bordoColAlt"
              style={{ width: "200px", maxWidth: "200px" }}
            >
              <BlockSkeleton tone="soft" width="72%" height={14} />
            </th>
            {Array.from({ length: 4 }).map((_, index) => (
              <th
                key={`brand-head-${index}`}
                className="tr pa2 bb bordoColAlt"
                style={{ width: "100px", maxWidth: "100px" }}
              >
                <div className="flex justify-end">
                  <BlockSkeleton tone="soft" width="82%" height={14} />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <tr key={`brand-row-skeleton-${rowIndex}`}>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton
                  tone="soft"
                  width={rowIndex === 4 ? "62%" : "78%"}
                  height={14}
                />
              </td>
              <td className="pa2 bb bordoColAlt tr">
                <div className="flex justify-end">
                  <BlockSkeleton tone="soft" width={48} height={14} />
                </div>
              </td>
              <td className="pa2 bb bordoColAlt tr">
                <div className="flex justify-end">
                  <BlockSkeleton tone="soft" width={52} height={14} />
                </div>
              </td>
              <td className="pa2 bb bordoColAlt tr">
                <div className="flex justify-end">
                  <BlockSkeleton tone="soft" width={58} height={14} />
                </div>
              </td>
              <td className="pa2 bb bordoColAlt tr">
                <div className="flex justify-end">
                  <BlockSkeleton tone="soft" width={54} height={28} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchBotteroMainTableSkeleton() {
  return (
    <div className="pa3 overflow-auto">
      <table className="collapse w-100 f7" style={{ minWidth: "1200px" }}>
        <thead>
          <tr>
            {[
              "48px",
              "90px",
              "80px",
              "120px",
              "100px",
              "130px",
              "180px",
              "200px",
              "100px",
              "100px",
            ].map((width, index) => (
              <th
                key={`main-head-${index}`}
                className="pa2 bb bordoColAlt tl"
                style={{ width, minWidth: width }}
              >
                <BlockSkeleton tone="soft" width="78%" height={14} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, rowIndex) => (
            <tr key={`main-row-${rowIndex}`}>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={18} height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={66} height={28} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={42} height={16} />
              </td>
              {Array.from({ length: 7 }).map((__, cellIndex) => (
                <td key={`main-cell-${rowIndex}-${cellIndex}`} className="pa2 bb bordoColAlt">
                  <BlockSkeleton
                    tone="soft"
                    width={cellIndex % 3 === 0 ? "72%" : cellIndex % 3 === 1 ? "88%" : "58%"}
                    height={20}
                    borderRadius={4}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchBotteroCandidatesTableSkeleton() {
  return (
    <div className="overflow-auto">
      <table className="collapse w-100 f7">
        <thead>
          <tr>
            {Array.from({ length: 10 }).map((_, index) => (
              <th key={`candidate-head-${index}`} className="tl pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="78%" height={14} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <tr key={`candidate-row-${rowIndex}`}>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={36} height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="86%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="72%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="88%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="82%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="70%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="56%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="72%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width="68%" height={18} />
              </td>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={92} height={28} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const normalizeModaFields = (fields) =>
  Array.isArray(fields)
    ? fields.filter(
        (field) =>
          field &&
          typeof field === "object" &&
          String(field.label || "").trim(),
      )
    : [];

const FILTER_LABELS = {
  search: "Ricerca libera",
  bsArticolo: "BS Articolo",
  bsGruppo1: "BS Gruppo1",
  bsGruppo2: "BS Gruppo2",
  bsGruppo3: "BS Gruppo3",
  vsGruppo1: "VS Gruppo1",
  vsGruppo2: "VS Gruppo2",
  vsGruppo3: "VS Gruppo3",
  vsGruppo4: "VS Gruppo4",
  bsCodModello: "BS Cod modello",
  bsCodColore: "BS Cod colore",
  bsBrand: "BS Brand",
  bsBrandConvertito: "BS Brand (convertito)",
  bsVariante: "BS Variante",
  bsStagione: "Stagione",
  bsNome: "BS Nome",
  tagDaAggiungere: "Tag assegnato",
  tagDaAggiungereEscluso: "Tag assegnato escluso",
  conflittiArtVarVs: "Possibili conflitti",
  tipoMatchArtVar: "Tipo match",
  quasiMatchSenzaTaglia: "Dati da integrare",
  gestioneParziale: "Gestione parziale",
  variantiCompletamenteIgnorate: "Varianti completamente ignorate",
  eanHistoricCancelledBs: "EAN storico con barcode BS annullato",
};

const formatGroupOptionLabel = (option) => {
  const code = String(option?.code || "").trim();
  const description = String(option?.description || "").trim();
  if (!code) return "";
  return description ? `${code} - ${description}` : code;
};
const buildBsModalTitleName = (row) => {
  const descrizione1 = String(row?.bsDescrizione1 || "").trim();
  const descrizione2 = String(row?.bsDescrizione2 || "").trim();
  const nomeCanonico = `${descrizione1}${descrizione2}`.trim();
  if (nomeCanonico) return nomeCanonico;
  return String(row?.bsNome || "").trim() || "-";
};

function MatchBotteroPage({ token, utente }) {
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingKpi, setLoadingKpi] = useState(false);
  const [showArticoliDaRifareModal, setShowArticoliDaRifareModal] =
    useState(false);
  const [articoliDaRifareRows, setArticoliDaRifareRows] = useState([]);
  const [loadingArticoliDaRifare, setLoadingArticoliDaRifare] = useState(false);
  const [articoliDaRifareError, setArticoliDaRifareError] = useState("");
  const [exportingArticoliDaRifare, setExportingArticoliDaRifare] =
    useState(false);
  const [exportingMatchCsv, setExportingMatchCsv] = useState(false);
  const [exportingVariantCsv, setExportingVariantCsv] = useState(false);
  const [showBulkCreateConflictModal, setShowBulkCreateConflictModal] =
    useState(false);
  const [bulkCreateConflictRows, setBulkCreateConflictRows] = useState([]);
  const [bulkCreateConflictSummary, setBulkCreateConflictSummary] = useState({
    successCount: 0,
    errorCount: 0,
  });
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("bsArticolo");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    bsGruppo1: [],
    bsGruppo2: [],
    bsGruppo3: [],
    vsGruppo1: [],
    vsGruppo2: [],
    vsGruppo3: [],
    vsGruppo4: [],
  });
  const [filterOptionsError, setFilterOptionsError] = useState("");
  const [selectedVariantRowKeys, setSelectedVariantRowKeys] = useState([]);
  const [brandSummaryRows, setBrandSummaryRows] = useState([]);
  const [loadingBrandSummary, setLoadingBrandSummary] = useState(false);
  const [brandSummaryError, setBrandSummaryError] = useState("");
  const [selectedBrandFromSummary, setSelectedBrandFromSummary] = useState("");
  const [isBrandSelectionStep, setIsBrandSelectionStep] = useState(true);
  const [activeRow, setActiveRow] = useState(null);
  const [variantRows, setVariantRows] = useState([]);
  const [eanAuditRows, setEanAuditRows] = useState([]);
  const [eanAuditLoading, setEanAuditLoading] = useState(false);
  const [eanAuditError, setEanAuditError] = useState("");
  const [showBarcodePanel, setShowBarcodePanel] = useState(false);
  const [showModaDataPanel, setShowModaDataPanel] = useState(false);
  const [modaDataLoading, setModaDataLoading] = useState(false);
  const [modaDataError, setModaDataError] = useState("");
  const [modaData, setModaData] = useState(null);
  const [selectedTagliaRow, setSelectedTagliaRow] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState("");
  const [variantActionError, setVariantActionError] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState({
    vsNome: "",
    vsVariante: "",
    vsCodProduttore: "",
    vsCodModello: "",
    vsBrand: "",
  });
  const [pendingMatchCandidate, setPendingMatchCandidate] = useState(null);
  const [sizeSelectionContext, setSizeSelectionContext] = useState(null);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sizeOptionsLoading, setSizeOptionsLoading] = useState(false);
  const [sizeStepError, setSizeStepError] = useState("");
  const [selectedOrdinaleTagliaVs, setSelectedOrdinaleTagliaVs] = useState("");
  const [showSaveMappingPrompt, setShowSaveMappingPrompt] = useState(false);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [createPromptMode, setCreatePromptMode] = useState("single");
  const [createPromptTargetRow, setCreatePromptTargetRow] = useState(null);
  const [createPromptSourceRow, setCreatePromptSourceRow] = useState(null);
  const [createPromptLoading, setCreatePromptLoading] = useState(false);
  const [createPromptError, setCreatePromptError] = useState("");
  const [createForm, setCreateForm] = useState({
    nuovoBrandSeCreazione: "",
    nuovoCodProdSeCreazione: "",
    nuovoCodModSeCreazione: "",
    nuovoCodColSeCreazione: "",
  });
  const [tagOptions, setTagOptions] = useState([]);
  const [tagOptionsLoading, setTagOptionsLoading] = useState(false);
  const [tagOptionsError, setTagOptionsError] = useState("");
  const [showTagManagerModal, setShowTagManagerModal] = useState(false);
  const [tagManagerSelectedCode, setTagManagerSelectedCode] = useState("");
  const [tagManagerError, setTagManagerError] = useState("");
  const [tagManagerLoading, setTagManagerLoading] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [bulkTagCode, setBulkTagCode] = useState("");
  const [bulkTagError, setBulkTagError] = useState("");
  const [bulkTagLoading, setBulkTagLoading] = useState(false);
  const [showBulkCreateOptionsModal, setShowBulkCreateOptionsModal] =
    useState(false);
  const [bulkCreateUseColorConsonants, setBulkCreateUseColorConsonants] =
    useState(true);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );
  const updatedByFromUtente = useMemo(
    () =>
      typeof utente?.username === "string" ? utente.username.trim() : "",
    [utente?.username],
  );
  const selectedBrandLabel = useMemo(() => {
    const selectedValue = String(selectedBrandFromSummary || "").trim();
    if (!selectedValue) return "";
    const matchedRow = brandSummaryRows.find(
      (row) =>
        String(row?.brandFilterValue || "")
          .trim()
          .toLowerCase() === selectedValue.toLowerCase(),
    );
    return String(matchedRow?.brand || selectedValue).trim();
  }, [brandSummaryRows, selectedBrandFromSummary]);
  const activeAssignedTagLabel = useMemo(() => {
    const code = String(filters.tagDaAggiungere || "").trim();
    if (!code) return "";
    if (code === "__NONE__") return "Nessun tag";
    const option = tagOptions.find(
      (item) => String(item?.code || "").trim() === code,
    );
    return option ? formatTagOptionLabel(option) : code;
  }, [filters.tagDaAggiungere, tagOptions]);
  const activeExcludedAssignedTagLabel = useMemo(() => {
    const code = String(filters.tagDaAggiungereEscluso || "").trim();
    if (!code) return "";
    const option = tagOptions.find(
      (item) => String(item?.code || "").trim() === code,
    );
    return option ? formatTagOptionLabel(option) : code;
  }, [filters.tagDaAggiungereEscluso, tagOptions]);
  const activeEanAuditModeLabel = useMemo(
    () =>
      String(filters.eanAuditMode || "").trim()
        ? formatEanAuditModeLabel(filters.eanAuditMode)
        : "",
    [filters.eanAuditMode],
  );
  const activeEanHistoricCancelledBsLabel = useMemo(
    () =>
      String(filters.eanHistoricCancelledBs || "").trim()
        ? formatEanHistoricCancelledBsLabel(filters.eanHistoricCancelledBs)
        : "",
    [filters.eanHistoricCancelledBs],
  );
  const activeVariantiCompletamenteIgnorateLabel = useMemo(
    () =>
      String(filters.variantiCompletamenteIgnorate || "").trim()
        ? formatVariantiCompletamenteIgnorateLabel(
            filters.variantiCompletamenteIgnorate,
          )
        : "",
    [filters.variantiCompletamenteIgnorate],
  );
  const compactAdvancedFilterSummary = useMemo(() => {
    const items = [];
    if (activeAssignedTagLabel) {
      items.push(`Tag assegnato: ${activeAssignedTagLabel}`);
    }
    if (activeExcludedAssignedTagLabel) {
      items.push(`Tag escluso: ${activeExcludedAssignedTagLabel}`);
    }
    if (filters.conflittiArtVarVs === "1") {
      items.push("Possibili conflitti: solo art-var con piu match VS");
    } else if (filters.conflittiArtVarVs === "0") {
      items.push("Possibili conflitti: solo altri casi");
    }
    if (activeEanAuditModeLabel) {
      items.push(activeEanAuditModeLabel);
    }
    if (activeEanHistoricCancelledBsLabel) {
      items.push(activeEanHistoricCancelledBsLabel);
    }
    if (activeVariantiCompletamenteIgnorateLabel) {
      items.push(activeVariantiCompletamenteIgnorateLabel);
    }
    return items;
  }, [
    activeAssignedTagLabel,
    activeEanAuditModeLabel,
    activeExcludedAssignedTagLabel,
    activeEanHistoricCancelledBsLabel,
    activeVariantiCompletamenteIgnorateLabel,
    filters.conflittiArtVarVs,
  ]);
  const matchedVsBarcodeRows = useMemo(() => {
    const seen = new Set();
    return variantRows.filter((row) => {
      if (
        row.codiceArticoloVsMatched === null ||
        row.numeroVarianteVigliettiMatched === null ||
        row.ordinaleTagliaVigliettiMatched === null
      ) {
        return false;
      }
      const key = [
        row.codiceArticoloVsMatched,
        row.numeroVarianteVigliettiMatched,
        row.ordinaleTagliaVigliettiMatched,
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [variantRows]);
  const currentVariantTags = useMemo(
    () => {
      const values = [];
      variantRows.forEach((row) => {
        parseTagList(row?.tagDaAggiungere).forEach((tag) => {
          values.push(tag);
        });
      });
      return Array.from(new Set(values));
    },
    [variantRows],
  );
  const currentVariantTagLabels = useMemo(() => {
    const lookup = new Map(
      tagOptions.map((option) => [
        String(option?.code || "").trim(),
        formatTagOptionLabel(option),
      ]),
    );
    return currentVariantTags.map((tag) => lookup.get(tag) || tag);
  }, [currentVariantTags, tagOptions]);
  const selectedTagliaAuditRow = useMemo(() => {
    if (!selectedTagliaRow) return null;
    return (
      eanAuditRows.find(
        (row) =>
          Number(row.ordinaleTagliaBottero) ===
          Number(selectedTagliaRow.ordinaleTagliaBottero),
      ) || null
    );
  }, [eanAuditRows, selectedTagliaRow]);
  useEffect(() => {
    if (selectedTagliaRow || !eanAuditRows.length) return;
    const firstAuditRow = eanAuditRows[0];
    const matchingVariantRow =
      variantRows.find(
        (row) =>
          Number(row.ordinaleTagliaBottero) ===
          Number(firstAuditRow?.ordinaleTagliaBottero),
      ) || firstAuditRow;
    if (matchingVariantRow) {
      setSelectedTagliaRow(matchingVariantRow);
    }
  }, [eanAuditRows, selectedTagliaRow, variantRows]);
  const modaBsFields = useMemo(
    () => normalizeModaFields(modaData?.bsFields),
    [modaData],
  );
  const modaVsMatches = useMemo(() => {
    const rawMatches = Array.isArray(modaData?.vsMatches)
      ? modaData.vsMatches
      : [];
    return rawMatches.map((match, index) => ({
      title: String(match?.title || `Match VS ${index + 1}`).trim(),
      fields: normalizeModaFields(match?.fields),
    }));
  }, [modaData]);
  const brandSummaryActiveFilters = useMemo(() => {
    const entries = [];
    [
      "search",
      "bsArticolo",
      "bsGruppo1",
      "bsGruppo2",
      "bsGruppo3",
      "vsGruppo1",
      "vsGruppo2",
      "vsGruppo3",
      "vsGruppo4",
      "bsCodModello",
      "bsCodColore",
      "bsVariante",
      "bsStagione",
      "bsNome",
      "tagDaAggiungere",
      "tagDaAggiungereEscluso",
    ].forEach((key) => {
      const value = String(filters?.[key] || "").trim();
      if (!value) return;
      entries.push({
        key,
        label: FILTER_LABELS[key] || key,
        value:
          key === "tagDaAggiungere" && activeAssignedTagLabel
            ? activeAssignedTagLabel
            : key === "tagDaAggiungereEscluso" && activeExcludedAssignedTagLabel
              ? activeExcludedAssignedTagLabel
            : value,
      });
    });
    if (filters.quasiMatchSenzaTaglia === "1") {
      entries.push({
        key: "quasiMatchSenzaTaglia",
        label: FILTER_LABELS.quasiMatchSenzaTaglia,
        value: "Solo art-var senza taglia VS",
      });
    } else if (filters.quasiMatchSenzaTaglia === "0") {
      entries.push({
        key: "quasiMatchSenzaTaglia",
        label: FILTER_LABELS.quasiMatchSenzaTaglia,
        value: "Solo altri casi",
      });
    }
    if (filters.gestioneParziale === "1") {
      entries.push({
        key: "gestioneParziale",
        label: FILTER_LABELS.gestioneParziale,
        value: "Solo art-var parziali (qualsiasi gestito)",
      });
    } else if (filters.gestioneParziale === "2") {
      entries.push({
        key: "gestioneParziale",
        label: FILTER_LABELS.gestioneParziale,
        value: "Solo match reale + da gestire",
      });
    } else if (filters.gestioneParziale === "0") {
      entries.push({
        key: "gestioneParziale",
        label: FILTER_LABELS.gestioneParziale,
        value: "Solo altri casi",
      });
    }
    if (filters.conflittiArtVarVs === "1") {
      entries.push({
        key: "conflittiArtVarVs",
        label: FILTER_LABELS.conflittiArtVarVs,
        value: "Solo art-var con piu match VS",
      });
    } else if (filters.conflittiArtVarVs === "0") {
      entries.push({
        key: "conflittiArtVarVs",
        label: FILTER_LABELS.conflittiArtVarVs,
        value: "Solo altri casi",
      });
    }
    if (filters.tipoMatchArtVar === "ean") {
      entries.push({
        key: "tipoMatchArtVar",
        label: FILTER_LABELS.tipoMatchArtVar,
        value: "EAN",
      });
    } else if (filters.tipoMatchArtVar === "codprod") {
      entries.push({
        key: "tipoMatchArtVar",
        label: FILTER_LABELS.tipoMatchArtVar,
        value: "Codice produttore",
      });
    } else if (filters.tipoMatchArtVar === "manuale") {
      entries.push({
        key: "tipoMatchArtVar",
        label: FILTER_LABELS.tipoMatchArtVar,
        value: "Manuale",
      });
    }
    if (filters.eanHistoricCancelledBs === "1") {
      entries.push({
        key: "eanHistoricCancelledBs",
        label: FILTER_LABELS.eanHistoricCancelledBs,
        value: "Solo interessati",
      });
    } else if (filters.eanHistoricCancelledBs === "0") {
      entries.push({
        key: "eanHistoricCancelledBs",
        label: FILTER_LABELS.eanHistoricCancelledBs,
        value: "Solo altri casi",
      });
    }
    if (filters.variantiCompletamenteIgnorate === "1") {
      entries.push({
        key: "variantiCompletamenteIgnorate",
        label: FILTER_LABELS.variantiCompletamenteIgnorate,
        value: "Solo interessate",
      });
    } else if (filters.variantiCompletamenteIgnorate === "0") {
      entries.push({
        key: "variantiCompletamenteIgnorate",
        label: FILTER_LABELS.variantiCompletamenteIgnorate,
        value: "Solo altri casi",
      });
    }
    return entries;
  }, [activeAssignedTagLabel, activeExcludedAssignedTagLabel, filters]);
  const brandSummaryOkLabel = useMemo(
    () => formatOkFilterLabel(filters.ok),
    [filters.ok],
  );
  const brandSummaryVariantCountLabel = useMemo(
    () => formatBrandSummaryVariantCountLabel(filters.ok),
    [filters.ok],
  );

  const loadFilterOptions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/filter-options`,
        {
          headers: authHeaders,
        },
      );
      const data = response?.data?.data ?? {};
      setFilterOptions({
        bsGruppo1: Array.isArray(data?.bsGruppo1) ? data.bsGruppo1 : [],
        bsGruppo2: Array.isArray(data?.bsGruppo2) ? data.bsGruppo2 : [],
        bsGruppo3: Array.isArray(data?.bsGruppo3) ? data.bsGruppo3 : [],
        vsGruppo1: Array.isArray(data?.vsGruppo1) ? data.vsGruppo1 : [],
        vsGruppo2: Array.isArray(data?.vsGruppo2) ? data.vsGruppo2 : [],
        vsGruppo3: Array.isArray(data?.vsGruppo3) ? data.vsGruppo3 : [],
        vsGruppo4: Array.isArray(data?.vsGruppo4) ? data.vsGruppo4 : [],
      });
      setFilterOptionsError("");
    } catch (err) {
      setFilterOptions({
        bsGruppo1: [],
        bsGruppo2: [],
        bsGruppo3: [],
        vsGruppo1: [],
        vsGruppo2: [],
        vsGruppo3: [],
        vsGruppo4: [],
      });
      setFilterOptionsError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento filtri gruppo BS",
      );
    }
  }, [authHeaders, token]);
  const loadTagOptions = useCallback(async () => {
    if (!token) return [];
    setTagOptionsLoading(true);
    setTagOptionsError("");
    try {
      const response = await axios.get(`${apiUrl}/moda/lookup/tag-articolo`, {
        headers: authHeaders,
      });
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setTagOptions(data);
      return data;
    } catch (err) {
      const message =
        err?.response?.data?.error ??
        err?.message ??
        "Errore caricamento tag articolo";
      setTagOptions([]);
      setTagOptionsError(message);
      throw err;
    } finally {
      setTagOptionsLoading(false);
    }
  }, [authHeaders, token]);

  const loadKpi = useCallback(async () => {
    if (!token) return;
    setLoadingKpi(true);
    try {
      const response = await axios.get(`${apiUrl}/moda/match-bottero/kpi`, {
        headers: authHeaders,
        params: {
          includeOutOfMovimenti: 1,
        },
      });
      setKpi(response?.data?.kpi ?? null);
    } catch (err) {
      setError(err?.response?.data?.error ?? err?.message ?? "Errore KPI");
    } finally {
      setLoadingKpi(false);
    }
  }, [authHeaders, token]);

  const loadArticoliDaRifare = useCallback(async () => {
    if (!token) return;
    setLoadingArticoliDaRifare(true);
    setArticoliDaRifareError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/articoli-da-rifare`,
        {
          headers: authHeaders,
        },
      );
      setArticoliDaRifareRows(
        Array.isArray(response?.data?.rows) ? response.data.rows : [],
      );
    } catch (err) {
      setArticoliDaRifareRows([]);
      setArticoliDaRifareError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento articoli da rifare",
      );
    } finally {
      setLoadingArticoliDaRifare(false);
    }
  }, [authHeaders, token]);
  const exportArticoliDaRifareCsv = useCallback(() => {
    if (!Array.isArray(articoliDaRifareRows) || !articoliDaRifareRows.length) {
      return;
    }
    setExportingArticoliDaRifare(true);
    try {
      const csvRows = articoliDaRifareRows.map((row) => ({
        vs_nome: row.vsNome || "",
        vs_articolo: row.vsArticolo || "",
        vs_variante: row.vsVariante || "",
        vs_cod_produttore: row.vsCodProduttore || "",
        vs_cod_modello: row.vsCodModello || "",
        vs_cod_colore: row.vsCodColore || "",
        vs_gruppo_taglia: `${row.vsTagliaGroupSigla || ""} - ${row.vsTagliaGroupDescrizione || ""}`,
        taglie_necessarie_bs: Array.isArray(row.taglieRichieste)
          ? row.taglieRichieste.join(" | ")
          : "",
        taglie_esistenti_vs: Array.isArray(row.taglieEsistentiVs)
          ? row.taglieEsistentiVs.join(" | ")
          : "",
        bs_articoli_matchati: Array.isArray(row.bsArticoli)
          ? row.bsArticoli
              .map(
                (item) =>
                  `${item.bsArticolo || "-"} - ${item.bsVariante || "-"}`,
              )
              .join(" | ")
          : "",
      }));
      const columns = Object.keys(csvRows[0] || {});
      const content = buildCsvContent(columns, csvRows);
      const today = new Date().toISOString().slice(0, 10);
      downloadCsvFile(content, `${today}-Articoli-VS-da-rifare.csv`);
    } finally {
      setExportingArticoliDaRifare(false);
    }
  }, [articoliDaRifareRows]);
  const exportMatchCsv = useCallback(async () => {
    if (!token) return;
    setExportingMatchCsv(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/moda/match-bottero/export`, {
        headers: authHeaders,
      });
      const exportRows = Array.isArray(response?.data?.rows)
        ? response.data.rows
        : [];
      if (!exportRows.length) {
        setError("Nessun match da esportare.");
        return;
      }
      const csvRows = exportRows.map((row) => ({
        codice_articolo_bs: row.codiceArticoloBs ?? "",
        bs_articolo: row.bsArticolo ?? "",
        numero_variante_bottero: row.numeroVarianteBottero ?? "",
        ordinale_taglia_bottero: row.ordinaleTagliaBottero ?? "",
        codice_articolo_vs: row.codiceArticoloVsMatched ?? "",
        vs_articolo: row.vsArticolo ?? "",
        numero_variante_viglietti: row.numeroVarianteVigliettiMatched ?? "",
        ordinale_taglia_viglietti: row.ordinaleTagliaVigliettiMatched ?? "",
        va_ignorato: Number(row.vaIgnorato || 0),
        andare_in_creazione: Number(row.andareInCreazione || 0),
        nuovo_brand_se_creazione: row.nuovoBrandSeCreazione ?? "",
        nuovo_cod_prod_se_creazione: row.nuovoCodProdSeCreazione ?? "",
        nuovo_cod_mod_se_creazione: row.nuovoCodModSeCreazione ?? "",
        nuovo_cod_col_se_creazione: row.nuovoCodColSeCreazione ?? "",
        tag_da_aggiungere: JSON.stringify(
          Array.isArray(row.tagDaAggiungere) ? row.tagDaAggiungere : [],
        ),
        barcode_esterno_bs: row.bsEanList ?? "",
        barcode_esterno_vs: row.vsEanList ?? "",
      }));
      const columns = Object.keys(csvRows[0] || {});
      const content = buildCsvContent(columns, csvRows);
      const today = new Date().toISOString().slice(0, 10);
      downloadCsvFile(content, `${today}-Match-Bottero.csv`);
    } catch (err) {
      setError(
        err?.response?.data?.error ?? err?.message ?? "Errore export match",
      );
    } finally {
      setExportingMatchCsv(false);
    }
  }, [authHeaders, token]);
  const exportVariantListCsv = useCallback(async () => {
    if (!token) return;
    setExportingVariantCsv(true);
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/export-variant-list`,
        {
          headers: authHeaders,
          params: {
            ...filters,
            sortBy,
            sortDirection,
          },
        },
      );
      const exportRows = Array.isArray(response?.data?.rows)
        ? response.data.rows
        : [];
      if (!exportRows.length) {
        setError("Nessun articolo da esportare con i filtri correnti.");
        return;
      }
      const csvRows = exportRows.map((row) => ({
        ok:
          Number(row.ok) === 1
            ? "OK"
            : `${Number(row.taglieSistemate ?? 0)}/${Number(row.totalTaglie ?? 0)}`,
        bs_cod_modello: row.bsCodModello ?? "",
        bs_cod_colore: row.bsCodColore ?? "",
        bs_nome: row.bsNome ?? "",
        bs_variante: row.bsVariante ?? "",
        bs_brand_convertito: row.bsBrandConvertito ?? "",
        stagione: row.bsStagione ?? "",
      }));
      const columns = Object.keys(csvRows[0] || {});
      const content = buildCsvContent(columns, csvRows);
      const today = new Date().toISOString().slice(0, 10);
      downloadCsvFile(content, `${today}-Match-Bottero-lista-articoli.csv`);
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore export lista articoli",
      );
    } finally {
      setExportingVariantCsv(false);
    }
  }, [authHeaders, filters, sortBy, sortDirection, token]);

  const loadRows = useCallback(async () => {
    if (!token) return;
    setLoadingRows(true);
    setError("");
    try {
      const params = {
        page,
        perPage,
        sortBy,
        sortDirection,
        view: "variant",
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim().length) {
          params[key] = value.trim();
        }
      });

      const response = await axios.get(`${apiUrl}/moda/match-bottero/list`, {
        headers: authHeaders,
        params,
      });

      const payload = response?.data ?? {};
      const list = Array.isArray(payload.rows) ? payload.rows : [];
      const mapped = list.map((row) => ({
        ...row,
        rowKey: `${row.codiceArticoloBs}-${row.numeroVarianteBottero}`,
      }));
      setRows(mapped);
      setSelectedVariantRowKeys([]);
      const total = Number(payload?.pagination?.total ?? 0);
      setTotalRows(Number.isFinite(total) ? total : 0);
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento tabella",
      );
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoadingRows(false);
    }
  }, [authHeaders, filters, page, perPage, sortBy, sortDirection, token]);

  const loadBrandSummary = useCallback(async () => {
    if (!token) return;
    setLoadingBrandSummary(true);
    setBrandSummaryError("");
    try {
      const { hasGiacenza: _legacyHasGiacenza, ...filtersWithoutGiacenza } =
        filters;
      const summaryFilters = {
        ...filtersWithoutGiacenza,
        bsBrand: "",
        bsBrandConvertito: "",
      };
      const params = {
        limit: 300,
      };
      Object.entries(summaryFilters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim().length) {
          params[key] = value.trim();
        }
      });
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/brand-summary`,
        {
          headers: authHeaders,
          params,
        },
      );
      const rows = Array.isArray(response?.data?.rows)
        ? response.data.rows
        : [];
      setBrandSummaryRows(rows);
    } catch (err) {
      setBrandSummaryRows([]);
      setBrandSummaryError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento brand",
      );
    } finally {
      setLoadingBrandSummary(false);
    }
  }, [authHeaders, filters, token]);

  useEffect(() => {
    if (isBrandSelectionStep) {
      loadKpi();
    }
  }, [isBrandSelectionStep, loadKpi]);

  useEffect(() => {
    if (!isBrandSelectionStep) {
      loadRows();
    }
  }, [isBrandSelectionStep, loadRows]);

  useEffect(() => {
    loadBrandSummary();
  }, [loadBrandSummary]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);
  useEffect(() => {
    loadTagOptions();
  }, [loadTagOptions]);

  const onSort = (sortField, direction) => {
    setSortBy(sortField || "bsArticolo");
    setSortDirection(normalizeSortDirection(direction));
    setPage(1);
  };

  const updateDraftFilter = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };
  const openAdvancedFilters = useCallback(() => {
    setDraftFilters(filters);
    setShowAdvancedFilters(true);
  }, [filters]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setSelectedBrandFromSummary((draftFilters.bsBrandConvertito || "").trim());
    setShowAdvancedFilters(false);
    setPage(1);
  };

  const resetFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
    setSelectedBrandFromSummary("");
    setIsBrandSelectionStep(true);
    setPage(1);
  };

  const applyBrandPreset = useCallback((brandRow) => {
    const value = String(brandRow?.brandFilterValue || "").trim();
    if (!value) return;
    setSelectedBrandFromSummary(value);
    setDraftFilters((prev) => ({
      ...prev,
      bsBrand: "",
      bsBrandConvertito: value,
    }));
    setFilters((prev) => ({
      ...prev,
      bsBrand: "",
      bsBrandConvertito: value,
    }));
    setIsBrandSelectionStep(false);
    setPage(1);
  }, []);

  const backToBrandSelection = useCallback(() => {
    setSelectedBrandFromSummary("");
    setDraftFilters((prev) => ({
      ...prev,
      bsBrand: "",
      bsBrandConvertito: "",
    }));
    setFilters((prev) => ({
      ...prev,
      bsBrand: "",
      bsBrandConvertito: "",
    }));
    setIsBrandSelectionStep(true);
    setPage(1);
    setSelectedVariantRowKeys([]);
  }, []);

  const loadCandidates = useCallback(
    async (row, searchOverride) => {
      if (!row) return;
      const effectiveSearch = searchOverride || candidateSearch;
      setCandidates([]);
      setCandidatesError("");
      setCandidatesLoading(true);
      try {
        const response = await axios.get(
          `${apiUrl}/moda/match-bottero/candidates`,
          {
            headers: authHeaders,
            params: {
              codiceArticoloBs: row.codiceArticoloBs,
              numeroVarianteBottero: row.numeroVarianteBottero,
              ordinaleTagliaBottero: row.ordinaleTagliaBottero,
              limit: 80,
              vsNome: effectiveSearch.vsNome.trim() || undefined,
              vsVariante: effectiveSearch.vsVariante.trim() || undefined,
              vsCodProduttore:
                effectiveSearch.vsCodProduttore.trim() || undefined,
              vsCodModello: effectiveSearch.vsCodModello.trim() || undefined,
              vsBrand: effectiveSearch.vsBrand.trim() || undefined,
            },
          },
        );
        setCandidates(response?.data?.data?.candidates || []);
      } catch (err) {
        setCandidatesError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore caricamento candidati",
        );
      } finally {
        setCandidatesLoading(false);
      }
    },
    [authHeaders, candidateSearch],
  );

  const loadVariantDetail = useCallback(
    async (row) => {
      if (!row) return;
      try {
        const response = await axios.get(
          `${apiUrl}/moda/match-bottero/variant-detail`,
          {
            headers: authHeaders,
            params: {
              codiceArticoloBs: row.codiceArticoloBs,
              numeroVarianteBottero: row.numeroVarianteBottero,
            },
          },
        );
        const detailRows = response?.data?.data?.rows;
        const normalized = Array.isArray(detailRows) ? detailRows : [];
        setVariantRows(normalized);
        if (normalized.length > 0) {
          const detailHead = normalized[0];
          setActiveRow((prev) => ({
            ...(prev || row),
            bsDescrizione1: detailHead.bsDescrizione1 ?? prev?.bsDescrizione1,
            bsDescrizione2: detailHead.bsDescrizione2 ?? prev?.bsDescrizione2,
            bsNome: detailHead.bsNome ?? prev?.bsNome,
          }));
        }
        if (selectedTagliaRow) {
          const refreshed = normalized.find(
            (item) =>
              Number(item.ordinaleTagliaBottero) ===
              Number(selectedTagliaRow.ordinaleTagliaBottero),
          );
          if (refreshed) {
            setSelectedTagliaRow(refreshed);
          }
        }
      } catch (err) {
        setCandidatesError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore caricamento taglie articolo",
        );
      }
    },
    [authHeaders, selectedTagliaRow],
  );

  const loadEanAuditDetail = useCallback(
    async (row) => {
      if (!row) return;
      setEanAuditLoading(true);
      setEanAuditError("");
      try {
        const response = await axios.get(
          `${apiUrl}/moda/match-bottero/ean-audit/list`,
          {
            headers: authHeaders,
            params: {
              page: 1,
              perPage: 100,
              sortBy: "ordinaleTagliaBottero",
              sortDirection: "asc",
              codiceArticoloBs: row.codiceArticoloBs,
              numeroVarianteBottero: row.numeroVarianteBottero,
              auditIsDifference: 1,
            },
          },
        );
        const list = Array.isArray(response?.data?.rows)
          ? response.data.rows
          : [];
        setEanAuditRows(list);
      } catch (err) {
        setEanAuditRows([]);
        setEanAuditError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore caricamento audit EAN",
        );
      } finally {
        setEanAuditLoading(false);
      }
    },
    [authHeaders],
  );

  const closeRowActions = useCallback(() => {
    setActiveRow(null);
    setCandidates([]);
    setCandidatesError("");
    setVariantActionError("");
    setCandidateSearch({
      vsNome: "",
      vsVariante: "",
      vsCodProduttore: "",
      vsCodModello: "",
      vsBrand: "",
    });
    setPendingMatchCandidate(null);
    setSizeSelectionContext(null);
    setSizeOptions([]);
    setSizeOptionsLoading(false);
    setSizeStepError("");
    setSelectedOrdinaleTagliaVs("");
    setShowSaveMappingPrompt(false);
    setShowCreatePrompt(false);
    setCreatePromptMode("single");
    setCreatePromptTargetRow(null);
    setCreatePromptLoading(false);
    setCreatePromptError("");
    setShowBarcodePanel(false);
    setShowModaDataPanel(false);
    setEanAuditRows([]);
    setEanAuditLoading(false);
    setEanAuditError("");
    setShowTagManagerModal(false);
    setTagManagerSelectedCode("");
    setTagManagerError("");
    setTagManagerLoading(false);
    setModaDataLoading(false);
    setModaDataError("");
    setModaData(null);
    setCreateForm({
      nuovoBrandSeCreazione: "",
      nuovoCodProdSeCreazione: "",
      nuovoCodModSeCreazione: "",
      nuovoCodColSeCreazione: "",
    });
    setCreatePromptSourceRow(null);
    setVariantRows([]);
    setSelectedTagliaRow(null);
  }, []);

  const closeMatchWorkPanels = useCallback(() => {
    setSelectedTagliaRow(null);
    setCandidates([]);
    setCandidatesError("");
    setPendingMatchCandidate(null);
    setSizeSelectionContext(null);
    setSizeOptions([]);
    setSizeOptionsLoading(false);
    setSizeStepError("");
    setSelectedOrdinaleTagliaVs("");
    setShowSaveMappingPrompt(false);
  }, []);

  const buildCreateDefaults = useCallback(
    (row) => {
      const nuovoCodModSeCreazione = String(
        row?.nuovoCodModSeCreazione ||
          row?.bsCodModelloPulito ||
          row?.bsCodModello ||
          row?.bsArticolo ||
          activeRow?.nuovoCodModSeCreazione ||
          activeRow?.bsCodModelloPulito ||
          activeRow?.bsCodModello ||
          activeRow?.bsArticolo ||
          "",
      ).trim();
      const nuovoCodColSeCreazione = String(
        row?.nuovoCodColSeCreazione ||
          row?.bsCodColorePulito ||
          row?.bsCodColore ||
          activeRow?.nuovoCodColSeCreazione ||
          activeRow?.bsCodColorePulito ||
          activeRow?.bsCodColore ||
          extractCodColFromBsVariante(
            row?.bsVariante || activeRow?.bsVariante || "",
          ) ||
          "",
      ).trim();
      const computedCodProd =
        nuovoCodModSeCreazione && nuovoCodColSeCreazione
          ? `${nuovoCodModSeCreazione}-${nuovoCodColSeCreazione}`
          : "";
      return {
        nuovoBrandSeCreazione: String(
          row?.nuovoBrandSeCreazione ||
            row?.bsBrandConvertitoSigla ||
            activeRow?.nuovoBrandSeCreazione ||
            activeRow?.bsBrandConvertitoSigla ||
            "",
        ).trim(),
        nuovoCodProdSeCreazione: String(
          row?.nuovoCodProdSeCreazione ||
            activeRow?.nuovoCodProdSeCreazione ||
            computedCodProd ||
            row?.bsArticolo ||
            row?.bsCodModello ||
            activeRow?.bsArticolo ||
            activeRow?.bsCodModello ||
            "",
        ).trim(),
        nuovoCodModSeCreazione,
        nuovoCodColSeCreazione,
      };
    },
    [activeRow],
  );
  const hasCalculatedColorCode = useCallback(
    (row) =>
      Boolean(
        String(
          row?.bsCodColorePulito || row?.bsCodColore || "",
        ).trim(),
      ),
    [],
  );
  const buildBulkCreateDataWithColorConsonants = useCallback(
    (row) => {
      const defaults = buildCreateDefaults(row);
      const suffix = buildColorConsonantsSuffix(row?.bsVariante || "");
      return {
        nuovoBrandSeCreazione: defaults.nuovoBrandSeCreazione,
        nuovoCodModSeCreazione: defaults.nuovoCodModSeCreazione,
        nuovoCodColSeCreazione: "",
        preserveEmptyCodColSeCreazione: true,
        nuovoCodProdSeCreazione: leftCreateValue(
          `${defaults.nuovoCodProdSeCreazione || ""}${suffix || ""}`,
          40,
        ),
      };
    },
    [buildCreateDefaults],
  );
  const appendColorConsonantsToCodProd = useCallback(() => {
    const suffix = buildColorConsonantsSuffix(
      createPromptSourceRow?.bsVariante ||
        selectedTagliaRow?.bsVariante ||
        activeRow?.bsVariante ||
        "",
    );
    if (!suffix) return;
    setCreateForm((prev) => {
      const currentValue = String(prev?.nuovoCodProdSeCreazione || "").trim();
      if (!currentValue || currentValue.endsWith(suffix)) {
        return prev;
      }
      return {
        ...prev,
        nuovoCodProdSeCreazione: `${currentValue}${suffix}`,
      };
    });
  }, [activeRow, createPromptSourceRow, selectedTagliaRow]);

  const openCreatePromptForRow = useCallback(
    (row) => {
      if (!row) return;
      closeMatchWorkPanels();
      setShowCreatePrompt(true);
      setCreatePromptMode("single");
      setCreatePromptTargetRow(row);
      setCreatePromptSourceRow(row);
      setCreatePromptError("");
      setCreateForm(buildCreateDefaults(row));
    },
    [buildCreateDefaults, closeMatchWorkPanels],
  );

  const openCreatePromptForAll = useCallback(() => {
    if (!variantRows.length) return;
    closeMatchWorkPanels();
    const sourceRow = variantRows[0] || activeRow;
    setShowCreatePrompt(true);
    setCreatePromptMode("bulk");
    setCreatePromptTargetRow(null);
    setCreatePromptSourceRow(sourceRow);
    setCreatePromptError("");
    setCreateForm(buildCreateDefaults(sourceRow));
  }, [activeRow, buildCreateDefaults, closeMatchWorkPanels, variantRows]);

  const cancelCreatePrompt = useCallback(() => {
    setShowCreatePrompt(false);
    setCreatePromptMode("single");
    setCreatePromptTargetRow(null);
    setCreatePromptSourceRow(null);
    setCreatePromptError("");
    setCreatePromptLoading(false);
  }, []);

  const applyCreateDecision = async () => {
    if (!activeRow) return;
    const normalizedCreateData = {
      nuovoBrandSeCreazione: String(
        createForm.nuovoBrandSeCreazione || "",
      ).trim(),
      nuovoCodProdSeCreazione: String(
        createForm.nuovoCodProdSeCreazione || "",
      ).trim(),
      nuovoCodModSeCreazione: String(
        createForm.nuovoCodModSeCreazione || "",
      ).trim(),
      nuovoCodColSeCreazione: String(
        createForm.nuovoCodColSeCreazione || "",
      ).trim(),
    };
    if (
      !normalizedCreateData.nuovoBrandSeCreazione ||
      !normalizedCreateData.nuovoCodProdSeCreazione ||
      !normalizedCreateData.nuovoCodModSeCreazione
    ) {
      setCreatePromptError(
        "Compila brand, codice produttore e codice modello.",
      );
      return;
    }
    const targets =
      createPromptMode === "bulk"
        ? variantRows
        : createPromptTargetRow
          ? [createPromptTargetRow]
          : [];
    if (!targets.length) {
      setCreatePromptError("Nessuna riga disponibile per la creazione.");
      return;
    }

    setCreatePromptLoading(true);
    setCreatePromptError("");
    setVariantActionError("");
    try {
      for (const row of targets) {
        await sendDecision({
          codiceArticoloBs: row.codiceArticoloBs,
          numeroVarianteBottero: row.numeroVarianteBottero,
          ordinaleTagliaBottero: row.ordinaleTagliaBottero,
          action: "set_create",
          createData: normalizedCreateData,
        });
      }
      await loadRows();
      await Promise.all([
        loadVariantDetail(activeRow),
        loadEanAuditDetail(activeRow),
      ]);
      cancelCreatePrompt();
    } catch (err) {
      setCreatePromptError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore aggiornamento creazione",
      );
    } finally {
      setCreatePromptLoading(false);
    }
  };

  const openSizeSelection = useCallback(
    async (baseRow, cand) => {
      if (!baseRow) return;
      setPendingMatchCandidate(cand);
      setSizeSelectionContext(null);
      setSizeOptions([]);
      setSizeStepError("");
      setSelectedOrdinaleTagliaVs("");
      setShowSaveMappingPrompt(false);
      setSizeOptionsLoading(true);
      try {
        const response = await axios.get(
          `${apiUrl}/moda/match-bottero/size-selection-context`,
          {
            headers: authHeaders,
            params: {
              codiceArticoloBs: baseRow.codiceArticoloBs,
              numeroVarianteBottero: baseRow.numeroVarianteBottero,
              ordinaleTagliaBottero: baseRow.ordinaleTagliaBottero,
              codiceArticoloVs: cand.codiceArticoloVs,
              numeroVarianteVs: cand.numeroVarianteVs,
            },
          },
        );
        const context = response?.data?.data ?? null;
        setSizeSelectionContext(context);
        const options = Array.isArray(context?.vsSizeOptions)
          ? context.vsSizeOptions
          : [];
        setSizeOptions(options);

        const bsTaglia = String(baseRow.bsTaglia || "")
          .trim()
          .toLowerCase();
        const preferredByLabel = options.find(
          (item) =>
            String(item.vsTaglia || "")
              .trim()
              .toLowerCase() === bsTaglia,
        );
        const preferred = preferredByLabel || null;
        if (preferred) {
          setSelectedOrdinaleTagliaVs(String(preferred.ordinaleTagliaVs));
        } else {
          setSelectedOrdinaleTagliaVs("");
        }
        if (!options.length) {
          setSizeStepError(
            "Nessuna taglia VS disponibile per questo articolo.",
          );
        }
      } catch (err) {
        setSizeStepError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore caricamento taglie VS",
        );
      } finally {
        setSizeOptionsLoading(false);
      }
    },
    [authHeaders],
  );

  const startMatchSelection = useCallback(
    async (cand) => {
      if (!selectedTagliaRow) return;
      await openSizeSelection(selectedTagliaRow, cand);
    },
    [openSizeSelection, selectedTagliaRow],
  );

  const openRowActions = useCallback(
    async (row) => {
      setActiveRow(row);
      setVariantRows([]);
      setEanAuditRows([]);
      setEanAuditError("");
      setEanAuditLoading(false);
      setShowBarcodePanel(false);
      setShowModaDataPanel(false);
      setModaDataLoading(false);
      setModaDataError("");
      setModaData(null);
      setSelectedTagliaRow(null);
      setVariantActionError("");
      setCandidateSearch({
        vsNome: "",
        vsVariante: "",
        vsCodProduttore: "",
        vsCodModello: "",
        vsBrand: "",
      });
      setPendingMatchCandidate(null);
      setSizeSelectionContext(null);
      setSizeOptions([]);
      setSizeOptionsLoading(false);
      setSizeStepError("");
      setSelectedOrdinaleTagliaVs("");
      setShowSaveMappingPrompt(false);
      setShowCreatePrompt(false);
      setCreatePromptMode("single");
      setCreatePromptTargetRow(null);
      setCreatePromptError("");
      setCreatePromptLoading(false);
      await Promise.all([loadVariantDetail(row), loadEanAuditDetail(row)]);
    },
    [loadEanAuditDetail, loadVariantDetail],
  );

  const loadModaContext = useCallback(
    async (row) => {
      if (!row) return null;
      setModaDataLoading(true);
      setModaDataError("");
      try {
        const response = await axios.get(
          `${apiUrl}/moda/match-bottero/variant-moda-context`,
          {
            headers: authHeaders,
            params: {
              codiceArticoloBs: row.codiceArticoloBs,
              numeroVarianteBottero: row.numeroVarianteBottero,
            },
          },
        );
        const nextData = response?.data?.data ?? null;
        setModaData(nextData);
        return nextData;
      } catch (err) {
        const message =
          err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento dati Moda";
        setModaDataError(message);
        return null;
      } finally {
        setModaDataLoading(false);
      }
    },
    [authHeaders],
  );

  const toggleModaDataPanel = useCallback(async () => {
    if (!showModaDataPanel) {
      setShowModaDataPanel(true);
      if (!modaData && !modaDataLoading && activeRow) {
        await loadModaContext(activeRow);
      }
      return;
    }
    setShowModaDataPanel(false);
  }, [activeRow, loadModaContext, modaData, modaDataLoading, showModaDataPanel]);

  const selectTagliaRow = useCallback(
    async (row) => {
      if (!row) return;
      setSelectedTagliaRow(row);
      setPendingMatchCandidate(null);
      setSizeSelectionContext(null);
      setSizeOptions([]);
      setSizeOptionsLoading(false);
      setSizeStepError("");
      setSelectedOrdinaleTagliaVs("");
      setShowSaveMappingPrompt(false);
      await loadCandidates(row);
    },
    [loadCandidates],
  );

  const sendDecision = useCallback(
    async (payload) => {
      const normalizedPayload =
        payload && typeof payload === "object"
          ? {
              ...payload,
              ...(updatedByFromUtente
                ? { updatedBy: updatedByFromUtente }
                : {}),
            }
          : payload;
      await axios.patch(
        `${apiUrl}/moda/match-bottero/decision`,
        normalizedPayload,
        {
          headers: authHeaders,
        },
      );
    },
    [authHeaders, updatedByFromUtente],
  );

  const maybeConfirmVsSizeGroupConflict = useCallback((err, payload) => {
    const details = err?.response?.data?.details;
    if (
      payload?.action !== "set_match" ||
      payload?.forceVsSizeGroupConflict === true ||
      details?.type !== "vs_size_group_conflict"
    ) {
      return false;
    }
    const existingGroups = Array.isArray(details?.existingVsSizeGroups)
      ? details.existingVsSizeGroups
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      : [];
    const requestedGroup = String(details?.requestedVsSizeGroup || "").trim();
    const confirmMessage = [
      err?.response?.data?.error ||
        "La variante BS ha gia taglie abbinate a un gruppo taglie VS diverso.",
      "",
      existingGroups.length
        ? `Gruppi VS gia presenti sulla variante BS: ${existingGroups.join(", ")}`
        : null,
      requestedGroup
        ? `Gruppo VS del candidato: ${requestedGroup}`
        : null,
      "",
      "Vuoi forzare comunque l'associazione per questa taglia?",
    ]
      .filter(Boolean)
      .join("\n");
    return window.confirm(confirmMessage);
  }, []);

  const sendVariantTagsOperation = useCallback(
    async ({ codiceArticoloBs, numeroVarianteBottero, operation, tags }) => {
      await axios.patch(
        `${apiUrl}/moda/match-bottero/variant-tags`,
        {
          codiceArticoloBs,
          numeroVarianteBottero,
          operation,
          tags,
          ...(updatedByFromUtente ? { updatedBy: updatedByFromUtente } : {}),
        },
        {
          headers: authHeaders,
        },
      );
    },
    [authHeaders, updatedByFromUtente],
  );

  const openTagManagerModal = useCallback(async () => {
    setShowTagManagerModal(true);
    setTagManagerError("");
    try {
      if (!tagOptions.length) {
        await loadTagOptions();
      }
    } catch (_error) {}
  }, [loadTagOptions, tagOptions.length]);

  const addTagToActiveVariant = useCallback(async () => {
    if (!activeRow) return;
    const code = String(tagManagerSelectedCode || "").trim();
    if (!code) {
      setTagManagerError("Seleziona un tag da aggiungere.");
      return;
    }
    setTagManagerLoading(true);
    setTagManagerError("");
    try {
      await sendVariantTagsOperation({
        codiceArticoloBs: activeRow.codiceArticoloBs,
        numeroVarianteBottero: activeRow.numeroVarianteBottero,
        operation: "add",
        tags: [code],
      });
      setTagManagerSelectedCode("");
      await loadRows();
      await Promise.all([
        loadVariantDetail(activeRow),
        loadEanAuditDetail(activeRow),
      ]);
    } catch (err) {
      setTagManagerError(
        err?.response?.data?.error ?? err?.message ?? "Errore aggiunta tag",
      );
    } finally {
      setTagManagerLoading(false);
    }
  }, [
    activeRow,
    loadEanAuditDetail,
    loadRows,
    loadVariantDetail,
    sendVariantTagsOperation,
    tagManagerSelectedCode,
  ]);

  const removeTagFromActiveVariant = useCallback(
    async (tagCode) => {
      if (!activeRow) return;
      setTagManagerLoading(true);
      setTagManagerError("");
      try {
        await sendVariantTagsOperation({
          codiceArticoloBs: activeRow.codiceArticoloBs,
          numeroVarianteBottero: activeRow.numeroVarianteBottero,
          operation: "remove",
          tags: [tagCode],
        });
        await loadRows();
        await Promise.all([
          loadVariantDetail(activeRow),
          loadEanAuditDetail(activeRow),
        ]);
      } catch (err) {
        setTagManagerError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore rimozione tag",
        );
      } finally {
        setTagManagerLoading(false);
      }
    },
    [
      activeRow,
      loadEanAuditDetail,
      loadRows,
      loadVariantDetail,
      sendVariantTagsOperation,
    ],
  );

  const openBulkTagModal = useCallback(async () => {
    setShowBulkTagModal(true);
    setBulkTagCode("");
    setBulkTagError("");
    try {
      if (!tagOptions.length) {
        await loadTagOptions();
      }
    } catch (_error) {}
  }, [loadTagOptions, tagOptions.length]);

  const applyTagOnSelectedVariants = useCallback(async () => {
    const code = String(bulkTagCode || "").trim();
    const selectedVariants = rows.filter((row) =>
      selectedVariantRowKeys.includes(row.rowKey),
    );
    if (!code) {
      setBulkTagError("Seleziona un tag da aggiungere.");
      return;
    }
    if (!selectedVariants.length) {
      setBulkTagError("Seleziona almeno un articolo-variante.");
      return;
    }

    setBulkTagLoading(true);
    setBulkTagError("");
    try {
      for (const variantRow of selectedVariants) {
        await sendVariantTagsOperation({
          codiceArticoloBs: variantRow.codiceArticoloBs,
          numeroVarianteBottero: variantRow.numeroVarianteBottero,
          operation: "add",
          tags: [code],
        });
      }
      setShowBulkTagModal(false);
      setBulkTagCode("");
      await loadRows();
    } catch (err) {
      setBulkTagError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore aggiunta tag massiva",
      );
    } finally {
      setBulkTagLoading(false);
    }
  }, [
    bulkTagCode,
    loadRows,
    rows,
    selectedVariantRowKeys,
    sendVariantTagsOperation,
  ]);

  const applyCreateOnSelectedVariants = async (mode = "hard") => {
    const selectedVariants = rows.filter((row) =>
      selectedVariantRowKeys.includes(row.rowKey),
    );
    if (!selectedVariants.length) {
      setError(
        "Seleziona almeno un articolo-variante da mandare in creazione.",
      );
      return;
    }
    setLoadingRows(true);
    setError("");
    setBulkCreateConflictRows([]);
    setBulkCreateConflictSummary({
      successCount: 0,
      errorCount: 0,
    });
    setShowBulkCreateConflictModal(false);
    try {
      const conflictRows = [];
      let successCount = 0;
      for (const variantRow of selectedVariants) {
        try {
          const createData =
            bulkCreateUseColorConsonants && !hasCalculatedColorCode(variantRow)
              ? buildBulkCreateDataWithColorConsonants(variantRow)
              : undefined;
          await sendDecision({
            codiceArticoloBs: variantRow.codiceArticoloBs,
            numeroVarianteBottero: variantRow.numeroVarianteBottero,
            ordinaleTagliaBottero: 0,
            action:
              mode === "soft"
                ? "set_create_all_sizes_soft"
                : "set_create_all_sizes",
            createData,
          });
          successCount += 1;
        } catch (err) {
          const status = Number(err?.response?.status ?? 0);
          if (status && status !== 401 && status !== 403) {
            conflictRows.push({
              bsArticolo: variantRow.bsArticolo || "",
              bsNome: variantRow.bsNome || "",
              bsVariante: variantRow.bsVariante || "",
              bsBrandConvertito: variantRow.bsBrandConvertito || "",
              status,
              message:
                err?.response?.data?.error ??
                err?.message ??
                "Errore creazione",
              details: err?.response?.data?.details ?? null,
            });
            continue;
          }
          throw err;
        }
      }
      await loadRows();
      setShowBulkCreateOptionsModal(false);
      setBulkCreateUseColorConsonants(false);
      if (conflictRows.length > 0) {
        setBulkCreateConflictRows(conflictRows);
        setBulkCreateConflictSummary({
          successCount,
          errorCount: conflictRows.length,
        });
        setShowBulkCreateConflictModal(true);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          mode === "soft"
            ? "Errore aggiornamento creazione soft selezionati"
            : "Errore aggiornamento creazione selezionati",
      );
    } finally {
      setLoadingRows(false);
    }
  };

  const checkMappingAlreadyExists = useCallback(
    async ({
      codiceArticoloBs,
      numeroVarianteBottero,
      ordinaleTagliaBottero,
      codiceArticoloVs,
      ordinaleTagliaVs,
    }) => {
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/mapping-exists`,
        {
          headers: authHeaders,
          params: {
            codiceArticoloBs,
            numeroVarianteBottero,
            ordinaleTagliaBottero,
            codiceArticoloVs,
            ordinaleTagliaVs,
          },
        },
      );
      return response?.data?.exists === true;
    },
    [authHeaders],
  );

  const applyDecision = useCallback(
    async (payload) => {
      if (!activeRow) return;
      try {
        await sendDecision(payload);
        await loadRows();
        if (activeRow) {
          await Promise.all([
            loadVariantDetail(activeRow),
            loadEanAuditDetail(activeRow),
          ]);
        }
        if (
          payload?.action === "set_match" ||
          payload?.action === "set_taglia_da_aggiungere" ||
          payload?.action === "set_articolo_da_rifare" ||
          payload?.action === "set_ignore"
        ) {
          closeMatchWorkPanels();
        }
      } catch (err) {
        if (maybeConfirmVsSizeGroupConflict(err, payload)) {
          try {
            await sendDecision({
              ...payload,
              forceVsSizeGroupConflict: true,
            });
            await loadRows();
            if (activeRow) {
              await Promise.all([
                loadVariantDetail(activeRow),
                loadEanAuditDetail(activeRow),
              ]);
            }
            if (
              payload?.action === "set_match" ||
              payload?.action === "set_taglia_da_aggiungere" ||
              payload?.action === "set_articolo_da_rifare" ||
              payload?.action === "set_ignore"
            ) {
              closeMatchWorkPanels();
            }
            return;
          } catch (forcedErr) {
            const forcedMessage =
              forcedErr?.response?.data?.error ??
              forcedErr?.message ??
              "Errore salvataggio";
            setCandidatesError(forcedMessage);
            setSizeStepError(forcedMessage);
            return;
          }
        }
        const message =
          err?.response?.data?.error ?? err?.message ?? "Errore salvataggio";
        setCandidatesError(message);
        setSizeStepError(message);
      }
    },
    [
      activeRow,
      loadEanAuditDetail,
      loadRows,
      loadVariantDetail,
      closeMatchWorkPanels,
      maybeConfirmVsSizeGroupConflict,
      sendDecision,
    ],
  );

  const applyBulkDecision = useCallback(
    async (action) => {
      if (!activeRow || !variantRows.length) return;
      setBulkActionLoading(true);
      setVariantActionError("");
      try {
        for (const row of variantRows) {
          await sendDecision({
            codiceArticoloBs: activeRow.codiceArticoloBs,
            numeroVarianteBottero: activeRow.numeroVarianteBottero,
            ordinaleTagliaBottero: row.ordinaleTagliaBottero,
            action,
          });
        }
        await loadRows();
        await Promise.all([
          loadVariantDetail(activeRow),
          loadEanAuditDetail(activeRow),
        ]);
      } catch (err) {
        setVariantActionError(
          err?.response?.data?.error ??
            err?.message ??
            "Errore aggiornamento taglie",
        );
      } finally {
        setBulkActionLoading(false);
      }
    },
    [
      activeRow,
      loadEanAuditDetail,
      loadRows,
      loadVariantDetail,
      sendDecision,
      variantRows,
    ],
  );

  const renderHeader = (label) => {
    const text = String(label || "").trim();
    return <span className="scrittaColAltScuro">{text}</span>;
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <input
            type="checkbox"
            checked={
              rows.length > 0 &&
              rows.every((row) => selectedVariantRowKeys.includes(row.rowKey))
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedVariantRowKeys(rows.map((row) => row.rowKey));
              } else {
                setSelectedVariantRowKeys([]);
              }
            }}
          />
        ),
        width: "48px",
        cell: (row) => (
          <input
            type="checkbox"
            checked={selectedVariantRowKeys.includes(row.rowKey)}
            onChange={(e) => {
              const key = row.rowKey;
              setSelectedVariantRowKeys((prev) => {
                const filtered = prev.filter((item) => item !== key);
                if (e.target.checked) {
                  return [...filtered, key];
                }
                return filtered;
              });
            }}
          />
        ),
      },
      {
        name: "",
        width: "90px",
        cell: (row) => (
          <button
            type="button"
            className="pointer bn br2 ph2 pv1 sfondoColScuro colBianco f7"
            onClick={() => openRowActions(row)}
          >
            Gestisci
          </button>
        ),
      },
      {
        name: "OK",
        selector: (row) => Number(row.ok) || 0,
        cell: (row) => {
          if (Number(row.ok) === 1) {
            return (
              <span className="green b" style={{ fontSize: 18 }}>
                ✓
              </span>
            );
          }
          const done = Number(row.taglieSistemate ?? 0);
          const total = Number(row.totalTaglie ?? 0);
          return (
            <span className="dark-gray b" style={{ fontSize: 13 }}>
              {done}/{total}
            </span>
          );
        },
        sortable: true,
        sortField: "ok",
        width: "80px",
        center: true,
      },
      {
        name: renderHeader("BS Cod modello"),
        selector: (row) => row.bsCodModello || "",
        cell: (row) => <span>{row.bsCodModello || ""}</span>,
        sortable: true,
        sortField: "bsCodModello",
        minWidth: "120px",
      },
      {
        name: renderHeader("BS Cod colore"),
        selector: (row) => row.bsCodColore || "",
        cell: (row) => <span>{row.bsCodColore || ""}</span>,
        sortable: true,
        sortField: "bsCodColore",
        minWidth: "100px",
      },
      {
        name: renderHeader("BS Kit (gruppo8)"),
        selector: (row) => row.bsTabGruppo8 || "",
        cell: (row) => <span>{row.bsTabGruppo8 || ""}</span>,
        minWidth: "130px",
      },
      /* {
        name: renderHeader("BS Articolo"),
        selector: (row) => row.bsArticolo || "",
        cell: (row) => <span>{row.bsArticolo || ""}</span>,
        sortable: true,
        sortField: "bsArticolo",
        minWidth: "120px",
      }, */
      {
        name: renderHeader("BS Nome"),
        selector: (row) => row.bsNome || "",
        cell: (row) => <span>{row.bsNome || ""}</span>,
        sortable: true,
        sortField: "bsNome",
        minWidth: "180px",
        wrap: true,
      },
      {
        name: renderHeader("BS Variante"),
        selector: (row) => row.bsVariante || "",
        cell: (row) => <span>{row.bsVariante || ""}</span>,
        sortable: true,
        sortField: "bsVariante",
        minWidth: "200px",
        wrap: true,
      },
      {
        name: renderHeader("BS Brand (convertito)"),
        selector: (row) => row.bsBrandConvertito || "",
        cell: (row) => <span>{row.bsBrandConvertito || ""}</span>,
        minWidth: "100px",
      },
      {
        name: renderHeader("Stagione"),
        selector: (row) => row.bsStagione || "",
        cell: (row) => <span>{row.bsStagione || "-"}</span>,
        sortable: true,
        sortField: "bsStagione",
        minWidth: "100px",
      },
    ],
    [openRowActions, rows, selectedVariantRowKeys],
  );

  const formatKpi = (value) => Number(value || 0).toLocaleString("it-IT");
  const triplesTotal = Number(kpi?.triplesTotal ?? 0);
  const triplesIgnored = Number(kpi?.triplesIgnored ?? 0);
  const triplesMatched = Number(kpi?.triplesMatched ?? 0);
  const triplesToCreate = Number(kpi?.triplesToCreate ?? 0);
  const triplesArticoloDaRifare = Number(kpi?.triplesArticoloDaRifare ?? 0);
  const triplesTagliaDaAggiungere = Number(
    kpi?.triplesTagliaDaAggiungere ?? 0,
  );
  const triplesGreenHandled = Number(
    kpi?.triplesGreenHandledExclusive ??
      triplesMatched + triplesArticoloDaRifare + triplesTagliaDaAggiungere,
  );
  const triplesToCreateExclusive = Number(
    kpi?.triplesToCreateExclusive ?? triplesToCreate,
  );
  const triplesIgnoredExclusive = Number(
    kpi?.triplesIgnoredExclusive ?? triplesIgnored,
  );
  const triplesToHandle = Number(kpi?.triplesToHandle ?? 0);
  const triplesGestiti =
    triplesGreenHandled + triplesToCreateExclusive + triplesIgnoredExclusive;
  const variantsTotal = Number(kpi?.variantsTotal ?? 0);
  const variantsGestiti = Number(kpi?.variantsHandledFull ?? 0);
  const pct = (value) =>
    triplesTotal > 0
      ? Math.max((Number(value || 0) / triplesTotal) * 100, 0)
      : 0;
  return (
    <div className="rootTema pa2 pa3-ns contentSubHeader">
      <div className="sfondoBiancoTema br3">
        {isBrandSelectionStep ? (
          <div className="pa2 pb0">
            <div className="flex flex-wrap justify-between">
            <div className="bl bordoColAlt br2 ph2 pt2 pb3 sfondoGrigioChiaroTema w-20">
              <div className="f7">Articoli-taglie gestiti</div>
              <div className="f4 b">
                {loadingKpi ? (
                  <div className="pt1">
                    <BlockSkeleton tone="soft" width={112} height={28} />
                  </div>
                ) : (
                  <>
                    <span className="green">{formatKpi(triplesGestiti)}</span>/
                    {formatKpi(triplesTotal)}
                  </>
                )}
              </div>
            </div>
            <div className="bl bordoColAlt br2 ph2 pt2 pb3 sfondoGrigioChiaroTema w-20">
              <div className="f7">Articoli-varianti gestiti interamente</div>
              <div className="f4 b">
                {loadingKpi ? (
                  <div className="pt1">
                    <BlockSkeleton tone="soft" width={112} height={28} />
                  </div>
                ) : (
                  <>
                    <span className="green">{formatKpi(variantsGestiti)}</span>/
                    {formatKpi(variantsTotal)}
                  </>
                )}
              </div>
            </div>
            <div className="bl bordoColAlt br2 ph2 pt2 pb3 sfondoGrigioChiaroTema w-third">
              {/* <div className="f7 mb2">Articoli-taglie</div> */}
              {loadingKpi ? (
                <MatchBotteroProgressSkeleton />
              ) : (
                <div className="flex justify-between">
                  <div className="f7 w-20">Progresso Articoli-taglie</div>
                  <div className="w-70 db">
                    <div className="flex justify-between mb2">
                      <div className="tl w-25">
                        <div className="f7">
                          <span className="f5 b green">
                            {formatKpi(triplesGreenHandled)}
                          </span>
                          {" gestite"}
                        </div>
                      </div>
                      <div className="tc w-25">
                        <div className="f7">
                          <span className="f5 b gray">
                            {formatKpi(triplesToHandle)}
                          </span>
                          {" da gestire"}
                        </div>
                      </div>
                      <div className="tr w-25">
                        <div className="f7">
                          <span className="f5 b orange">
                            {formatKpi(triplesToCreateExclusive)}
                          </span>
                          {" in creazione"}
                        </div>
                      </div>
                      <div className="tr w-25">
                        <div className="f7">
                          <span className="f5 b near-black">
                            {formatKpi(triplesIgnoredExclusive)}
                          </span>
                          {" ignorate"}
                        </div>
                      </div>
                    </div>
                    <div
                      className="w-100 br2 overflow-hidden"
                      style={{ height: 14 }}
                    >
                      <div className="h-100 flex">
                        <div
                          className="tastoVerde"
                          style={{ width: `${pct(triplesGreenHandled)}%` }}
                        />
                        <div
                          className="sfondoColChiaro"
                          style={{ width: `${pct(triplesToHandle)}%` }}
                        />
                        <div
                          className="tastoArancio"
                          style={{ width: `${pct(triplesToCreateExclusive)}%` }}
                        />
                        <div
                          className="sfondoColScuro"
                          style={{ width: `${pct(triplesIgnoredExclusive)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="bl bordoColAlt br2 ph2 pt2 pb3 sfondoGrigioChiaroTema flex flex-column items-stretch justify-end w-10">
              <button
                type="button"
                className={`${themeButtonClasses.primary} mb2`}
                onClick={exportMatchCsv}
                disabled={exportingMatchCsv}
              >
                {exportingMatchCsv ? "Esporto..." : "Esporta match"}
              </button>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColAltScuro white"
                onClick={async () => {
                  setShowArticoliDaRifareModal(true);
                  await loadArticoliDaRifare();
                }}
              >
                Articoli da rifare
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <div className="pa3 sfondoColAltScuro mb3 flex justify-between items-center">
          {!isBrandSelectionStep && selectedVariantRowKeys.length > 0 ? (
            <div className="flex items-center flex-wrap">
              <button
                type="button"
                className={themeButtonClasses.warning}
                disabled={loadingRows}
                onClick={() => setShowBulkCreateOptionsModal(true)}
              >
                In creazione selezionati ({selectedVariantRowKeys.length})
              </button>
              <button
                type="button"
                className={`${themeButtonClasses.medium} ml2`}
                disabled={loadingRows}
                onClick={openBulkTagModal}
              >
                Aggiungi tag
              </button>
            </div>
          ) : (
            <div className="f3 b">
              Match articoli Bottero
              {!isBrandSelectionStep && selectedBrandLabel
                ? ` - ${selectedBrandLabel}`
                : ""}
            </div>
          )}
          {isBrandSelectionStep ? (
            <div className="flex flex-wrap items-end">
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
                onClick={() => {
                  loadKpi();
                  loadBrandSummary();
                }}
              >
                Aggiorna brand
              </button>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 ml2"
                onClick={openAdvancedFilters}
              >
                Modifica filtri
              </button>
              <button
                type="button"
                className={`${themeButtonClasses.neutral} mb2 ml2`}
                onClick={resetFilters}
              >
                Reset filtri
              </button>
            </div>
          ) : (
            <form
              className="flex flex-wrap items-end"
              onSubmit={(e) => {
                e.preventDefault();
                applyFilters();
              }}
            >
              <div className="mr3 mb2">
                <label className="f7 db mb1">BS Articolo</label>
                <input
                  className="input-reset ba bordoColAlt br2 pa2"
                  value={draftFilters.bsArticolo}
                  onChange={(e) =>
                    updateDraftFilter("bsArticolo", e.target.value)
                  }
                />
              </div>
              <div className="mr3 mb2">
                <label className="f7 db mb1">BS Nome</label>
                <input
                  className="input-reset ba bordoColAlt br2 pa2"
                  value={draftFilters.bsNome}
                  onChange={(e) => updateDraftFilter("bsNome", e.target.value)}
                />
              </div>
              <div className="mr3 mb2">
                <label className="f7 db mb1">OK</label>
                <select
                  className="input-reset ba bordoColAlt br2 pa2"
                  value={draftFilters.ok}
                  onChange={(e) => updateDraftFilter("ok", e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="1">Solo OK</option>
                  <option value="0">Solo non OK</option>
                </select>
              </div>
              <div className="mr3 mb2">
                <label className="f7 db mb1">Dati da integrare</label>
                <select
                  className="input-reset ba bordoColAlt br2 pa2"
                  value={draftFilters.quasiMatchSenzaTaglia}
                  onChange={(e) =>
                    updateDraftFilter("quasiMatchSenzaTaglia", e.target.value)
                  }
                >
                  <option value="">Tutti</option>
                  <option value="1">Solo art-var senza taglia VS</option>
                  <option value="0">Solo altri casi</option>
                </select>
              </div>
              <div className="mr3 mb2">
                <label className="f7 db mb1">Gestione parziale</label>
                <select
                  className="input-reset ba bordoColAlt br2 pa2"
                  value={draftFilters.gestioneParziale}
                  onChange={(e) =>
                    updateDraftFilter("gestioneParziale", e.target.value)
                  }
                >
                  <option value="">Tutti</option>
                  <option value="1">
                    Solo art-var parziali (qualsiasi gestito)
                  </option>
                  <option value="2">Solo match reale + da gestire</option>
                  <option value="0">Solo altri casi</option>
                </select>
              </div>
              <button
                type="submit"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
              >
                Cerca
              </button>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 ml2 "
                onClick={resetFilters}
              >
                Reset filtri
              </button>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 ml2 mr3"
                onClick={() => {
                  loadRows();
                }}
              >
                Aggiorna
              </button>
              <button
                type="button"
                className={`${themeButtonClasses.medium} mb2 ml2`}
                onClick={openAdvancedFilters}
              >
                Ricerca avanzata
              </button>
              <button
                type="button"
                className={`${themeButtonClasses.medium} mb2 ml2`}
                onClick={exportVariantListCsv}
                disabled={exportingVariantCsv}
                title="Esporta CSV con tutti i risultati dei filtri correnti"
              >
                {exportingVariantCsv ? "Esporto..." : "⤓ CSV"}
              </button>
              <button
                type="button"
                className={`${themeButtonClasses.neutral} mb2 ml2`}
                onClick={backToBrandSelection}
              >
                Cambia brand
              </button>
            </form>
          )}
          {error ? <div className="dark-red mt2 f6">{error}</div> : null}
        </div>

        {isBrandSelectionStep ? (
          <div className="pa3 pt0">
            <div className="mb3 pa3 br2 sfondoGrigioChiaroTema">
              <div className="f6 b mb2">Filtri applicati al riepilogo brand</div>
              <div className="f7 dark-gray mb2">
                La lista brand usa gli stessi filtri della lista articoli e
                ignora i filtri brand.
              </div>
              <div className="flex flex-wrap items-center">
                <span className={themePillClasses.primary}>
                  OK: {brandSummaryOkLabel}
                </span>
                <span className={themePillClasses.neutral}>
                  Brand: tutti
                </span>
                {brandSummaryActiveFilters.map((item) => (
                  <span
                    key={`${item.key}-${item.value}`}
                    className={themePillClasses.info}
                  >
                    {item.label}: {item.value}
                  </span>
                ))}
                {!brandSummaryActiveFilters.length ? (
                  <span className="f7 dark-gray mb2">
                    Nessun altro filtro attivo.
                  </span>
                ) : null}
              </div>
            </div>
            <div className="f6 b mb2">
              Seleziona brand ({brandSummaryOkLabel})
            </div>
            {brandSummaryError ? (
              <div className="dark-red mb2 f7">{brandSummaryError}</div>
            ) : null}
            {loadingBrandSummary ? (
              <MatchBotteroBrandSelectionTableSkeleton />
            ) : null}
            {!loadingBrandSummary && brandSummaryRows.length ? (
              <div className="overflow-auto mb2">
                <table
                  className="collapse f7"
                  style={{ tableLayout: "fixed", width: "600px" }}
                >
                  <thead>
                    <tr>
                      <th
                        className="tl pa2 bb bordoColAlt"
                        title="Brand"
                        style={{
                          width: "200px",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Brand
                      </th>
                      <th
                        className="tr pa2 bb bordoColAlt"
                        title={brandSummaryVariantCountLabel}
                        style={{
                          width: "100px",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {brandSummaryVariantCountLabel}
                      </th>
                      <th
                        className="tr pa2 bb bordoColAlt"
                        title="Taglie da gestire"
                        style={{
                          width: "100px",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Taglie da gestire
                      </th>
                      <th
                        className="tr pa2 bb bordoColAlt"
                        title="Art matchati con taglie da selezionare"
                        style={{
                          width: "100px",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Art matchati con taglie da selezionare
                      </th>
                      <th
                        className="tr pa2 bb bordoColAlt"
                        title="Azione"
                        style={{
                          width: "100px",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Azione
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandSummaryRows.map((row) => {
                      const brandValue = String(
                        row.brandFilterValue || "",
                      ).trim();
                      const isSelected =
                        brandValue &&
                        brandValue.toLowerCase() ===
                          String(selectedBrandFromSummary || "")
                            .trim()
                            .toLowerCase();
                      return (
                        <tr
                          key={`${row.brand}-${brandValue || "empty"}`}
                          className={isSelected ? "sfondoColChiaro" : ""}
                        >
                          <td
                            className="pa2 bb bordoColAlt"
                            title={row.brand || "-"}
                            style={{
                              width: "200px",
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.brand || "-"}
                          </td>
                          <td
                            className="pa2 bb bordoColAlt tr"
                            style={{ width: "100px", maxWidth: "100px" }}
                          >
                            {Number(row.variantsNonOk || 0).toLocaleString(
                              "it-IT",
                            )}
                          </td>
                          <td
                            className="pa2 bb bordoColAlt tr"
                            style={{ width: "100px", maxWidth: "100px" }}
                          >
                            {Number(row.triplesNonOk || 0).toLocaleString(
                              "it-IT",
                            )}
                          </td>
                          <td
                            className="pa2 bb bordoColAlt tr"
                            style={{ width: "100px", maxWidth: "100px" }}
                          >
                            {Number(
                              row.variantsConTaglieDaSelezionare || 0,
                            ).toLocaleString("it-IT")}
                          </td>
                          <td
                            className="pa2 bb bordoColAlt tr"
                            style={{ width: "100px", maxWidth: "100px" }}
                          >
                            <button
                              type="button"
                              className="pointer bn br2 ph2 pv1 sfondoColScuro colBianco"
                              disabled={!brandValue}
                              onClick={() => applyBrandPreset(row)}
                            >
                              Apri
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {compactAdvancedFilterSummary.length ? (
              <div className="pa2 ph3 f7 sfondoGrigioChiaroTema bb bordoColAlt flex items-center justify-between">
                <div>
                  <span className="b mr2">Filtri avanzati attivi:</span>
                  {compactAdvancedFilterSummary.join(" | ")}
                </div>
                <button
                  type="button"
                  className={`${themeButtonClasses.alt} f7`}
                  onClick={openAdvancedFilters}
                >
                  Modifica
                </button>
              </div>
            ) : null}
            <DataTableBase
              columns={columns}
              data={rows}
              progressPending={loadingRows}
              progressComponent={<MatchBotteroMainTableSkeleton />}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={PER_PAGE_OPTIONS}
              paginationDefaultPage={page}
              onChangeRowsPerPage={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
              onChangePage={(newPage) => setPage(newPage)}
              sortServer
              onSort={(column, direction) =>
                onSort(column.sortField ?? "bsArticolo", direction)
              }
              defaultSortField={sortBy}
              defaultSortAsc={sortDirection !== "desc"}
              dense
              highlightOnHover
              keyField="rowKey"
              noDataComponent="Nessuna riga disponibile con i filtri correnti."
            />
          </>
        )}
      </div>

      {showAdvancedFilters ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={() => setShowAdvancedFilters(false)}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{
              width: "min(1500px, 98vw)",
              maxHeight: "88vh",
              margin: "4vh auto",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 bb bordoColAlt flex items-center justify-between">
              <h3 className="ma0">Ricerca avanzata</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                onClick={() => setShowAdvancedFilters(false)}
              >
                Chiudi
              </button>
            </div>
            <div
              className="pa3"
              style={{ maxHeight: "calc(88vh - 62px)", overflow: "auto" }}
            >
              <div className="flex flex-wrap items-start">
                <div className="mr4 mb3" style={{ minWidth: "220px" }}>
                  <div className="f6 b mb2">Ricerca BS</div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Ricerca libera</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.search}
                      onChange={(e) =>
                        updateDraftFilter("search", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Articolo</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsArticolo}
                      onChange={(e) =>
                        updateDraftFilter("bsArticolo", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Tag assegnato</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.tagDaAggiungere}
                      disabled={tagOptionsLoading}
                      onChange={(e) =>
                        updateDraftFilter("tagDaAggiungere", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      <option value="__NONE__">Nessun tag</option>
                      {tagOptions.map((option) => (
                        <option
                          key={`filter-tag-${option.code}`}
                          value={option.code}
                        >
                          {formatTagOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                    {tagOptionsError ? (
                      <div className="dark-red f7 mt1">{tagOptionsError}</div>
                    ) : null}
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Tag da escludere</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.tagDaAggiungereEscluso}
                      disabled={tagOptionsLoading}
                      onChange={(e) =>
                        updateDraftFilter(
                          "tagDaAggiungereEscluso",
                          e.target.value,
                        )
                      }
                    >
                      <option value="">Nessuno</option>
                      {tagOptions.map((option) => (
                        <option
                          key={`filter-tag-excluded-${option.code}`}
                          value={option.code}
                        >
                          {formatTagOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Nome</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsNome}
                      onChange={(e) =>
                        updateDraftFilter("bsNome", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Cod modello</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsCodModello}
                      onChange={(e) =>
                        updateDraftFilter("bsCodModello", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Cod colore</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsCodColore}
                      onChange={(e) =>
                        updateDraftFilter("bsCodColore", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Brand</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsBrand}
                      onChange={(e) =>
                        updateDraftFilter("bsBrand", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Brand (convertito)</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsBrandConvertito}
                      onChange={(e) =>
                        updateDraftFilter("bsBrandConvertito", e.target.value)
                      }
                    />
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Stagione</label>
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsStagione}
                      onChange={(e) =>
                        updateDraftFilter("bsStagione", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="mr4 mb3" style={{ minWidth: "220px" }}>
                  <div className="f6 b mb2">Stato</div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">OK</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.ok}
                      onChange={(e) => updateDraftFilter("ok", e.target.value)}
                    >
                      <option value="">Tutti</option>
                      <option value="1">Solo OK</option>
                      <option value="0">Solo non OK</option>
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Possibili conflitti</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.conflittiArtVarVs}
                      onChange={(e) =>
                        updateDraftFilter("conflittiArtVarVs", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      <option value="1">Solo art-var con piu match VS</option>
                      <option value="0">Solo altri casi</option>
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Tipo match</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.tipoMatchArtVar}
                      onChange={(e) =>
                        updateDraftFilter("tipoMatchArtVar", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      <option value="ean">EAN</option>
                      <option value="codprod">Codice produttore</option>
                      <option value="manuale">Manuale</option>
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Dati da integrare</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.quasiMatchSenzaTaglia}
                      onChange={(e) =>
                        updateDraftFilter("quasiMatchSenzaTaglia", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      <option value="1">Solo art-var senza taglia VS</option>
                      <option value="0">Solo altri casi</option>
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Gestione parziale</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.gestioneParziale}
                      onChange={(e) =>
                        updateDraftFilter("gestioneParziale", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      <option value="1">
                        Solo art-var parziali (qualsiasi gestito)
                      </option>
                      <option value="2">Solo match reale + da gestire</option>
                      <option value="0">Solo altri casi</option>
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">
                      Varianti completamente ignorate
                    </label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.variantiCompletamenteIgnorate}
                      onChange={(e) =>
                        updateDraftFilter(
                          "variantiCompletamenteIgnorate",
                          e.target.value,
                        )
                      }
                    >
                      {VARIANTI_COMPLETAMENTE_IGNORATE_OPTIONS.map((option) => (
                        <option
                          key={`advanced-varianti-completamente-ignorate-${option.value || "all"}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">Audit EAN</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.eanAuditMode}
                      onChange={(e) =>
                        updateDraftFilter("eanAuditMode", e.target.value)
                      }
                    >
                      {EAN_AUDIT_MODE_OPTIONS.map((option) => (
                        <option
                          key={`advanced-ean-audit-mode-${option.value || "all"}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">EAN storico annullato</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.eanHistoricCancelledBs}
                      onChange={(e) =>
                        updateDraftFilter(
                          "eanHistoricCancelledBs",
                          e.target.value,
                        )
                      }
                    >
                      {EAN_HISTORIC_CANCELLED_BS_OPTIONS.map((option) => (
                        <option
                          key={`advanced-historic-cancelled-ean-${option.value || "all"}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mr4 mb3" style={{ minWidth: "220px" }}>
                  <div className="f6 b mb2">Gruppi BS</div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Gruppo1</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsGruppo1}
                      onChange={(e) =>
                        updateDraftFilter("bsGruppo1", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.bsGruppo1.map((option) => (
                        <option key={`g1-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Gruppo2</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsGruppo2}
                      onChange={(e) =>
                        updateDraftFilter("bsGruppo2", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.bsGruppo2.map((option) => (
                        <option key={`g2-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">BS Gruppo3</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.bsGruppo3}
                      onChange={(e) =>
                        updateDraftFilter("bsGruppo3", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.bsGruppo3.map((option) => (
                        <option key={`g3-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb3" style={{ minWidth: "220px" }}>
                  <div className="f6 b mb2">Gruppi VS</div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">VS Gruppo1</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.vsGruppo1}
                      onChange={(e) =>
                        updateDraftFilter("vsGruppo1", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.vsGruppo1.map((option) => (
                        <option key={`vg1-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">VS Gruppo2</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.vsGruppo2}
                      onChange={(e) =>
                        updateDraftFilter("vsGruppo2", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.vsGruppo2.map((option) => (
                        <option key={`vg2-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">VS Gruppo3</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.vsGruppo3}
                      onChange={(e) =>
                        updateDraftFilter("vsGruppo3", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.vsGruppo3.map((option) => (
                        <option key={`vg3-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mr3 mb2">
                    <label className="f7 db mb1">VS Gruppo4</label>
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters.vsGruppo4}
                      onChange={(e) =>
                        updateDraftFilter("vsGruppo4", e.target.value)
                      }
                    >
                      <option value="">Tutti</option>
                      {filterOptions.vsGruppo4.map((option) => (
                        <option key={`vg4-${option.code}`} value={option.code}>
                          {formatGroupOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {filterOptionsError ? (
                <div className="dark-red mt2 f7">{filterOptionsError}</div>
              ) : null}
              <div className="mt3">
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2"
                  onClick={applyFilters}
                >
                  Applica filtri
                </button>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                  onClick={resetFilters}
                >
                  Reset filtri
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showArticoliDaRifareModal ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={() => setShowArticoliDaRifareModal(false)}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{
              width: "min(1400px, 96vw)",
              maxHeight: "88vh",
              margin: "4vh auto",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 bb bordoColAlt flex items-center justify-between">
              <h3 className="ma0">Articoli VS da rifare</h3>
              <div className="flex items-center">
                <button
                  type="button"
                  className={`${themeButtonClasses.primary} mr2`}
                  disabled={
                    exportingArticoliDaRifare || !articoliDaRifareRows.length
                  }
                  onClick={exportArticoliDaRifareCsv}
                >
                  {exportingArticoliDaRifare ? "Esporto..." : "Scarica CSV"}
                </button>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2"
                  onClick={loadArticoliDaRifare}
                >
                  Aggiorna
                </button>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                  onClick={() => setShowArticoliDaRifareModal(false)}
                >
                  Chiudi
                </button>
              </div>
            </div>
            <div
              className="pa3"
              style={{ maxHeight: "calc(88vh - 62px)", overflow: "auto" }}
            >
              {articoliDaRifareError ? (
                <div className="dark-red mb2 f7">{articoliDaRifareError}</div>
              ) : null}
              {loadingArticoliDaRifare ? (
                <div className="f7 dark-gray">Caricamento articoli...</div>
              ) : null}
              {!loadingArticoliDaRifare && !articoliDaRifareRows.length ? (
                <div className="f7 dark-gray">Nessun articolo da rifare.</div>
              ) : null}
              {!loadingArticoliDaRifare && articoliDaRifareRows.length ? (
                <div className="overflow-auto">
                  <table className="collapse w-100 f7">
                    <thead>
                      <tr>
                        <th className="tl pa2 bb bordoColAlt">VS Nome</th>
                        <th className="tl pa2 bb bordoColAlt">VS Articolo</th>
                        <th className="tl pa2 bb bordoColAlt">VS Variante</th>
                        <th className="tl pa2 bb bordoColAlt">
                          VS Cod produttore
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Gruppo taglia VS
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Taglie necessarie
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Taglie esistenti VS
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Articoli BS matchati
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {articoliDaRifareRows.map((row) => (
                        <tr
                          key={`${row.codiceArticoloVs}-${row.numeroVarianteVs}`}
                        >
                          <td className="pa2 bb bordoColAlt">
                            {row.vsNome || "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {row.vsArticolo || "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {row.vsVariante || "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {row.vsCodProduttore || "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {row.vsTagliaGroupSigla || "-"}
                            {" - "}
                            {row.vsTagliaGroupDescrizione || "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {Array.isArray(row.taglieRichieste) &&
                            row.taglieRichieste.length
                              ? row.taglieRichieste.join(", ")
                              : "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {Array.isArray(row.taglieEsistentiVs) &&
                            row.taglieEsistentiVs.length
                              ? row.taglieEsistentiVs.join(", ")
                              : "-"}
                          </td>
                          <td className="pa2 bb bordoColAlt">
                            {Array.isArray(row.bsArticoli) &&
                            row.bsArticoli.length ? (
                              <div>
                                {row.bsArticoli.map((item, idx) => (
                                  <div
                                    key={`${item.bsArticolo}-${item.bsVariante}-${idx}`}
                                  >
                                    {item.bsArticolo || "-"} -{" "}
                                    {item.bsVariante || "-"}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {showBulkCreateConflictModal ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={() => setShowBulkCreateConflictModal(false)}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{
              width: "min(1400px, 96vw)",
              maxHeight: "88vh",
              margin: "4vh auto",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 bb bordoColAlt flex items-center justify-between">
              <h3 className="ma0">Esiti creazione massiva</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                onClick={() => setShowBulkCreateConflictModal(false)}
              >
                Chiudi
              </button>
            </div>
            <div
              className="pa3"
              style={{ maxHeight: "calc(88vh - 62px)", overflow: "auto" }}
            >
              <div className="mb3 f7">
                <b>{bulkCreateConflictSummary.successCount}</b>
                {" articoli-varianti messi in creazione, "}
                <b>{bulkCreateConflictSummary.errorCount}</b>
                {" con errore da gestire manualmente."}
              </div>
              {!bulkCreateConflictRows.length ? (
                <div className="f7 dark-gray">Nessun errore da mostrare.</div>
              ) : (
                <div className="overflow-auto">
                  <table className="collapse w-100 f7">
                    <thead>
                      <tr>
                        <th className="tl pa2 bb bordoColAlt">BS Articolo</th>
                        <th className="tl pa2 bb bordoColAlt">BS Nome</th>
                        <th className="tl pa2 bb bordoColAlt">BS Variante</th>
                        <th className="tl pa2 bb bordoColAlt">Brand tentato</th>
                        <th className="tl pa2 bb bordoColAlt">
                          Cod produttore tentato
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Cod modello tentato
                        </th>
                        <th className="tl pa2 bb bordoColAlt">
                          Cod colore tentato
                        </th>
                        <th className="tl pa2 bb bordoColAlt">Dettagli</th>
                        <th className="tl pa2 bb bordoColAlt">Messaggio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkCreateConflictRows.map((row, index) => {
                        const details = row.details || {};
                        const attemptedCreate = details.attemptedCreate || {};
                        const existingVsArticle = details.existingVsArticle || {};
                        const validationRows = Array.isArray(details.validation)
                          ? details.validation
                          : [];
                        return (
                          <tr
                            key={`${row.bsArticolo}-${row.bsVariante}-${index}`}
                          >
                            <td className="pa2 bb bordoColAlt">
                              {row.bsArticolo || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {row.bsNome || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {row.bsVariante || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {attemptedCreate.brand || row.bsBrandConvertito || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {attemptedCreate.codiceProduttore || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {attemptedCreate.codiceModello || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              {attemptedCreate.codiceColore || "-"}
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              <div>
                                <div>
                                  <b>Tipo:</b>{" "}
                                  {details.type || `http_${row.status || "err"}`}
                                </div>
                                {existingVsArticle.vsArticolo ? (
                                  <div className="mt1">
                                    <div>
                                      <b>VS esistente:</b>{" "}
                                      {existingVsArticle.vsArticolo || "-"}
                                    </div>
                                    <div>
                                      <b>Nome:</b>{" "}
                                      {existingVsArticle.vsNome || "-"}
                                    </div>
                                    <div>
                                      <b>Brand:</b>{" "}
                                      {existingVsArticle.vsBrand ||
                                        existingVsArticle.vsBrandSigla ||
                                        "-"}
                                    </div>
                                    <div>
                                      <b>Cod produttore:</b>{" "}
                                      {existingVsArticle.vsCodProduttore || "-"}
                                    </div>
                                    <div>
                                      <b>Cod modello:</b>{" "}
                                      {existingVsArticle.vsCodModello || "-"}
                                    </div>
                                    <div>
                                      <b>ID VS:</b>{" "}
                                      {existingVsArticle.codiceArticoloVs || "-"}
                                    </div>
                                  </div>
                                ) : null}
                                {validationRows.length ? (
                                  <div className="mt1">
                                    <b>Validazioni:</b>
                                    {validationRows.map((item, validationIndex) => (
                                      <div
                                        key={`${row.bsArticolo}-${index}-${validationIndex}`}
                                      >
                                        {item.message || `${item.field}: ${item.rule}`}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                                {!existingVsArticle.vsArticolo &&
                                !validationRows.length ? (
                                  <div>-</div>
                                ) : null}
                              </div>
                            </td>
                            <td className="pa2 bb bordoColAlt">
                              <div>
                                <div>{row.message || "-"}</div>
                                {Number(row.status) ? (
                                  <div className="dark-gray mt1">
                                    HTTP {row.status}
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showBulkCreateOptionsModal ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={() => {
            if (!loadingRows) {
              setShowBulkCreateOptionsModal(false);
              setBulkCreateUseColorConsonants(false);
            }
          }}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{ width: "min(620px, 96vw)", margin: "12vh auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 bb bordoColAlt flex items-center justify-between">
              <h3 className="ma0">In creazione selezionati</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                disabled={loadingRows}
                onClick={() => {
                  setShowBulkCreateOptionsModal(false);
                  setBulkCreateUseColorConsonants(false);
                }}
              >
                Chiudi
              </button>
            </div>
            <div className="pa3">
              <div className="f7 mb3">
                Articoli-varianti selezionati:{" "}
                <b>{selectedVariantRowKeys.length}</b>
              </div>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  className="mr2 mt1"
                  checked={bulkCreateUseColorConsonants}
                  disabled={loadingRows}
                  onChange={(e) =>
                    setBulkCreateUseColorConsonants(e.target.checked)
                  }
                />
                <span className="f7 lh-copy">
                  Per gli articoli senza codice colore calcolato, aggiungi a
                  <b> Codice produttore </b>
                  il suffisso formato da <b>-</b> + consonanti uppercase della
                  descrizione colore. In questo caso
                  <b> Codice colore </b>
                  resta vuoto.
                </span>
              </label>
              <div className="mt3">
                <button
                  type="button"
                  className={`${themeButtonClasses.warning} mr2`}
                  disabled={loadingRows}
                  onClick={() => applyCreateOnSelectedVariants("hard")}
                >
                  {loadingRows ? "Salvataggio..." : "Conferma creazione"}
                </button>
                <button
                  type="button"
                  className={themeButtonClasses.medium}
                  disabled={loadingRows}
                  onClick={() => applyCreateOnSelectedVariants("soft")}
                >
                  {loadingRows
                    ? "Salvataggio..."
                    : "Tentativo soft: solo con barcode su tutte le taglie"}
                </button>
              </div>
              <div className="f7 dark-gray mt2">
                L&apos;opzione soft mette in creazione un articolo-variante
                solo se ogni taglia presente in Bottero ha almeno un barcode
                fornitore associato.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showBulkTagModal ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={() => {
            if (!bulkTagLoading) {
              setShowBulkTagModal(false);
              setBulkTagError("");
            }
          }}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{ width: "min(520px, 96vw)", margin: "12vh auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 bb bordoColAlt flex items-center justify-between">
              <h3 className="ma0">Aggiungi tag ai selezionati</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                disabled={bulkTagLoading}
                onClick={() => {
                  setShowBulkTagModal(false);
                  setBulkTagError("");
                }}
              >
                Chiudi
              </button>
            </div>
            <div className="pa3">
              <div className="f7 mb3">
                Articoli-varianti selezionati:{" "}
                <b>{selectedVariantRowKeys.length}</b>
              </div>
              <label className="f7 db mb1">Tag</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2 w-100"
                value={bulkTagCode}
                disabled={bulkTagLoading || tagOptionsLoading}
                onChange={(e) => setBulkTagCode(e.target.value)}
              >
                <option value="">Seleziona tag</option>
                {tagOptions.map((option) => (
                  <option key={`bulk-tag-${option.code}`} value={option.code}>
                    {formatTagOptionLabel(option)}
                  </option>
                ))}
              </select>
              {tagOptionsError ? (
                <div className="dark-red f6 mt2">{tagOptionsError}</div>
              ) : null}
              {bulkTagError ? (
                <div className="dark-red f6 mt2">{bulkTagError}</div>
              ) : null}
              <div className="mt3">
                <button
                  type="button"
                  className={themeButtonClasses.medium}
                  disabled={bulkTagLoading || tagOptionsLoading}
                  onClick={applyTagOnSelectedVariants}
                >
                  {bulkTagLoading ? "Salvataggio..." : "Conferma aggiunta tag"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeRow ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema"
          onClick={closeRowActions}
        >
          <div
            className="sfondoBiancoTema br3 "
            style={{
              width: "min(1600px, 98vw)",
              maxHeight: "90vh",
              margin: "3vh auto",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ph3 pv2 flex items-center justify-between">
              <h3 className="">Gestione riga non OK</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                onClick={closeRowActions}
              >
                Chiudi
              </button>
            </div>
            <div
              className="pa3"
              style={{ maxHeight: "calc(90vh - 62px)", overflow: "auto" }}
            >
              <div className="mb3">
                <h2 className="ma0 mb2">
                  {buildBsModalTitleName(activeRow)} -{" "}
                  {activeRow.bsArticolo || "-"} - {activeRow.bsVariante || "-"}
                </h2>
                <div className="flex flex-wrap">
                  <div className="mr3 mb1">
                    Brand convertito:{" "}
                    <b>{activeRow.bsBrandConvertito || "-"}</b>
                  </div>
                  <div className="mr3 mb1">
                    BS Cod modello: <b>{activeRow.bsCodModello || "-"}</b>
                  </div>
                  <div className="mr3 mb1">
                    BS Cod colore: <b>{activeRow.bsCodColore || "-"}</b>
                  </div>
                  <div className="mr3 mb1">
                    BS Kit (gruppo8): <b>{activeRow.bsTabGruppo8 || "-"}</b>
                  </div>
                <div className="mr3 mb1">
                  Taglie sistemate:{" "}
                  <b>
                    {activeRow.taglieSistemate ?? 0}/
                    {activeRow.totalTaglie ?? 0}
                  </b>
                </div>
                <div className="mr3 mb1">
                  Tag da aggiungere:{" "}
                  <b>
                    {currentVariantTagLabels.length
                      ? currentVariantTagLabels.join(", ")
                      : "-"}
                  </b>
                </div>
              </div>
            </div>

              <div className="mb3">
                <button
                  type="button"
                  className={`${themeButtonClasses.warning} mr2`}
                  disabled={bulkActionLoading || !variantRows.length}
                  onClick={openCreatePromptForAll}
                >
                  In Creazione (tutte)
                </button>
                <button
                  type="button"
                  className={themeButtonClasses.danger}
                  disabled={bulkActionLoading || !variantRows.length}
                  onClick={() => {
                    const confirmed = window.confirm(
                      "ATTENZIONE! Verranno rimossi tutti i match da tutte le taglie di questo articolo, anche quelli stabiliti in passato o dalle procedure automatiche (i match in questione sono elencati nella prima tabella di questa finestra). Sei proprio sicurə?",
                    );
                    if (confirmed) {
                      applyBulkDecision("clear_decision");
                    }
                  }}
                >
                  Elimina tutti i match
                </button>
                <button
                  type="button"
                  className={`${themeButtonClasses.medium} ml2`}
                  disabled={!variantRows.length}
                  onClick={() => setShowBarcodePanel((prev) => !prev)}
                >
                  {showBarcodePanel ? "Nascondi barcode" : "Mostra barcode"}
                </button>
                <button
                  type="button"
                  className={`${themeButtonClasses.medium} ml2`}
                  disabled={!activeRow || modaDataLoading}
                  onClick={toggleModaDataPanel}
                >
                  {showModaDataPanel
                    ? "Nascondi dati Moda"
                    : modaDataLoading
                      ? "Carico dati Moda..."
                      : "Mostra dati Moda"}
                </button>
                <button
                  type="button"
                  className={`${themeButtonClasses.medium} ml2`}
                  disabled={!activeRow}
                  onClick={openTagManagerModal}
                >
                  Gestisci tag
                </button>
                {bulkActionLoading ? (
                  <span className="ml2 f7 dark-gray">
                    Aggiornamento taglie...
                  </span>
                ) : null}
                {variantActionError ? (
                  <div className="dark-red mt2 f6">{variantActionError}</div>
                ) : null}
              </div>

              <div className="mb3">
                <div className="f6 b mb2">Audit EAN di oggi</div>
                {eanAuditLoading ? (
                  <BlockSkeleton tone="soft" width="100%" height={96} />
                ) : eanAuditError ? (
                  <div className="dark-red f6">{eanAuditError}</div>
                ) : eanAuditRows.length ? (
                  <div className="ba bordoColAlt br2 pa3 sfondoBiancoTema">
                    <div className="flex flex-wrap items-start justify-between mb3">
                      <div className="mr3 mb2">
                        <div className="f7 dark-gray">Taglie con differenze audit</div>
                        <div className="f4 b">
                          {Number(eanAuditRows.length || 0).toLocaleString("it-IT")}
                        </div>
                      </div>
                      {selectedTagliaAuditRow ? (
                        <div className="flex-auto mb2">
                          <div className="f7 dark-gray mb1">
                            Focus sulla taglia selezionata
                          </div>
                          <div className="f6 b mb1">
                            {selectedTagliaAuditRow.bsTaglia || "-"}:{" "}
                            {formatEanAuditDiffTypeLabel(
                              selectedTagliaAuditRow.auditDiffType,
                            )}
                          </div>
                          <div className="f7 mb1">
                            Stato corrente:{" "}
                            <b>
                              {formatEanAuditCurrentDecisionLabel(
                                selectedTagliaAuditRow.currentDecisionType,
                              )}
                            </b>
                          </div>
                          <div className="f7 mb1">
                            Simulazione EAN:{" "}
                            <b>
                              {selectedTagliaAuditRow.simulatedEanCandidateStatus ===
                              "multiple"
                                ? "Ambigua"
                                : buildEanAuditSimulatedTargetLabel(
                                    selectedTagliaAuditRow,
                                  )}
                            </b>
                          </div>
                          <div className="f7 mb1">
                            Diagnosi barcode:{" "}
                            <b>
                              {buildEanAuditBarcodeSummary(
                                selectedTagliaAuditRow,
                              )}
                            </b>
                          </div>
                          <div className="f7 mb1">
                            Barcode BS coinvolti:{" "}
                            <b>
                              {Array.isArray(
                                selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics,
                              ) &&
                              selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics.length
                                ? selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics
                                    .map((item) => item.barcode)
                                    .join(", ")
                                : String(
                                    selectedTagliaAuditRow.simulatedEanMatchedBarcodeList ||
                                      selectedTagliaAuditRow.bsEanList ||
                                      "",
                                  ).trim() || "-"}
                            </b>
                          </div>
                          <div className="f7 mb1">
                            Barcode VS match corrente:{" "}
                            <b>
                              {String(selectedTagliaAuditRow.vsEanList || "").trim() ||
                                "-"}
                            </b>
                          </div>
                          <div className="f7 mb1">
                            Codici VS puntati dalla simulazione:{" "}
                            <b>
                              {buildEanAuditVsCodesSummary(selectedTagliaAuditRow)}
                            </b>
                          </div>
                          <div className="f7">
                            Blocker sync:{" "}
                            <b>
                              {Array.isArray(
                                selectedTagliaAuditRow.simulatedEanSyncBlockers,
                              ) &&
                              selectedTagliaAuditRow.simulatedEanSyncBlockers.length
                                ? selectedTagliaAuditRow.simulatedEanSyncBlockers.join(
                                    ", ",
                                  )
                                : "nessuno"}
                            </b>
                          </div>
                          {Array.isArray(
                            selectedTagliaAuditRow.simulatedEanAmbiguityReasons,
                          ) &&
                          selectedTagliaAuditRow.simulatedEanAmbiguityReasons.length ? (
                            <div className="mt2">
                              {selectedTagliaAuditRow.simulatedEanAmbiguityReasons.map(
                                (reason) => (
                                  <span
                                    key={`ean-ambiguity-${selectedTagliaAuditRow.ordinaleTagliaBottero}-${reason}`}
                                    className={themePillClasses.neutral}
                                  >
                                    {formatEanAuditAmbiguityReasonLabel(reason)}
                                  </span>
                                ),
                              )}
                            </div>
                          ) : null}
                          {Array.isArray(
                            selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics,
                          ) &&
                          selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics.length ? (
                            <div className="overflow-auto mt3">
                              <table className="collapse w-100 f7">
                                <thead>
                                  <tr>
                                    <th className="tl pa2 bb bordoColAlt">
                                      Barcode BS
                                    </th>
                                    <th className="tl pa2 bb bordoColAlt">
                                      Codici VS
                                    </th>
                                    <th className="tl pa2 bb bordoColAlt">
                                      Target VS trovati
                                    </th>
                                    <th className="tl pa2 bb bordoColAlt">
                                      Diagnosi
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedTagliaAuditRow.simulatedEanBarcodeDiagnostics.map(
                                    (item) => (
                                      <tr
                                        key={`ean-audit-barcode-detail-${selectedTagliaAuditRow.ordinaleTagliaBottero}-${item.barcode}`}
                                      >
                                        <td className="pa2 bb bordoColAlt b">
                                          {item.barcode}
                                        </td>
                                        <td className="pa2 bb bordoColAlt">
                                          {extractVsCodesFromTargetLabels(
                                            item.vsTargetLabels,
                                          ).join(", ") || "-"}
                                        </td>
                                        <td className="pa2 bb bordoColAlt">
                                          {Array.isArray(item.vsTargetLabels) &&
                                          item.vsTargetLabels.length
                                            ? item.vsTargetLabels.join(" ; ")
                                            : "-"}
                                        </td>
                                        <td className="pa2 bb bordoColAlt">
                                          {Number(item.vsTargetCount || 0) > 1
                                            ? "Questo barcode BS punta a piu target VS"
                                            : "Questo barcode BS punta a un solo target VS"}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="f7 dark-gray mb2">
                          Seleziona una taglia per vedere il focus puntuale.
                        </div>
                      )}
                    </div>
                    <div className="overflow-auto">
                      <table className="collapse w-100 f7">
                        <thead>
                          <tr>
                            <th className="tl pa2 bb bordoColAlt">Ord.</th>
                            <th className="tl pa2 bb bordoColAlt">BS Taglia</th>
                            <th className="tl pa2 bb bordoColAlt">Delta audit</th>
                            <th className="tl pa2 bb bordoColAlt">Stato corrente</th>
                            <th className="tl pa2 bb bordoColAlt">Target EAN simulato</th>
                            <th className="tl pa2 bb bordoColAlt">Diagnosi barcode</th>
                            <th className="tl pa2 bb bordoColAlt">Blocker sync</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eanAuditRows.map((auditRow) => {
                            const isFocused =
                              Number(auditRow.ordinaleTagliaBottero) ===
                              Number(selectedTagliaRow?.ordinaleTagliaBottero);
                            return (
                              <tr
                                key={`ean-audit-${auditRow.codiceArticoloBs}-${auditRow.numeroVarianteBottero}-${auditRow.ordinaleTagliaBottero}`}
                                className={isFocused ? "sfondoColChiaro" : ""}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  const matchingVariantRow =
                                    variantRows.find(
                                      (row) =>
                                        Number(row.ordinaleTagliaBottero) ===
                                        Number(auditRow.ordinaleTagliaBottero),
                                    ) || auditRow;
                                  setSelectedTagliaRow(matchingVariantRow);
                                }}
                              >
                                <td className="pa2 bb bordoColAlt b">
                                  {auditRow.ordinaleTagliaBottero}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {auditRow.bsTaglia || "-"}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {formatEanAuditDiffTypeLabel(
                                    auditRow.auditDiffType,
                                  )}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {formatEanAuditCurrentDecisionLabel(
                                    auditRow.currentDecisionType,
                                  )}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {auditRow.simulatedEanCandidateStatus === "multiple"
                                    ? "Ambigua"
                                    : buildEanAuditSimulatedTargetLabel(auditRow)}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {buildEanAuditBarcodeSummary(auditRow)}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {Array.isArray(auditRow.simulatedEanSyncBlockers) &&
                                  auditRow.simulatedEanSyncBlockers.length
                                    ? auditRow.simulatedEanSyncBlockers.join(", ")
                                    : "-"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="f7 dark-gray">
                    Nessuna differenza audit EAN rilevata per questa variante.
                  </div>
                )}
              </div>

              {showBarcodePanel ? (
                <div className="mb3">
                  <div className="f6 b mb2">Barcode taglie</div>
                  <div className="flex flex-wrap">
                    <div className="w-100 w-50-l pr0 pr3-l mb3 mb0-l">
                      <div className="f7 b mb2">Barcode Bottero</div>
                      <div className="overflow-auto">
                        <table className="collapse w-100 f7">
                          <thead>
                            <tr>
                              <th className="tl pa2 bb bordoColAlt">Ord.</th>
                              <th className="tl pa2 bb bordoColAlt">BS Taglia</th>
                              <th className="tl pa2 bb bordoColAlt">Barcode BS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {variantRows.map((row) => (
                              <tr key={`bs-barcode-${row.ordinaleTagliaBottero}`}>
                                <td className="pa2 bb bordoColAlt b">
                                  {row.ordinaleTagliaBottero}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {row.bsTaglia || "-"}
                                </td>
                                <td className="pa2 bb bordoColAlt">
                                  {normalizeBarcodeDetails(
                                    row.bsEanDetails,
                                    row.bsEanList,
                                  ).map((item) => (
                                    <div key={`${item.barcode}-${item.annullato}`}>
                                      {item.barcode}
                                      {item.annullato !== 0 ? (
                                        <span className="dark-red b ml1">
                                          (annullato)
                                        </span>
                                      ) : null}
                                    </div>
                                  ))}
                                  {!normalizeBarcodeDetails(
                                    row.bsEanDetails,
                                    row.bsEanList,
                                  ).length
                                    ? "-"
                                    : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="w-100 w-50-l">
                      <div className="f7 b mb2">Barcode VS matchati</div>
                      <div className="overflow-auto">
                        <table className="collapse w-100 f7">
                          <thead>
                            <tr>
                              <th className="tl pa2 bb bordoColAlt">Ord. VS</th>
                              <th className="tl pa2 bb bordoColAlt">VS Taglia</th>
                              <th className="tl pa2 bb bordoColAlt">Barcode VS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {matchedVsBarcodeRows.length ? (
                              matchedVsBarcodeRows.map((row) => (
                                <tr
                                  key={`vs-barcode-${row.codiceArticoloVsMatched}-${row.numeroVarianteVigliettiMatched}-${row.ordinaleTagliaVigliettiMatched}`}
                                >
                                  <td className="pa2 bb bordoColAlt b">
                                    {row.ordinaleTagliaVigliettiMatched || "-"}
                                  </td>
                                  <td className="pa2 bb bordoColAlt">
                                    {row.vsTaglia || "-"}
                                  </td>
                                  <td className="pa2 bb bordoColAlt">
                                    {normalizeBarcodeDetails(
                                      row.vsEanDetails,
                                      row.vsEanList,
                                    ).map((item) => (
                                      <div key={`${item.barcode}-${item.annullato}`}>
                                        {item.barcode}
                                        {item.annullato !== 0 ? (
                                          <span className="dark-red b ml1">
                                            (annullato)
                                          </span>
                                        ) : null}
                                      </div>
                                    ))}
                                    {!normalizeBarcodeDetails(
                                      row.vsEanDetails,
                                      row.vsEanList,
                                    ).length
                                      ? "-"
                                      : null}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  className="pa2 bb bordoColAlt mid-gray"
                                  colSpan={3}
                                >
                                  Nessuna taglia VS matchata.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {showModaDataPanel ? (
                <div className="mb3">
                  <div className="f6 b mb2">Dati Moda articolo-variante</div>
                  {modaDataError ? (
                    <div className="dark-red mb2 f6">{modaDataError}</div>
                  ) : null}
                  {modaDataLoading && !modaData ? (
                    <div className="dark-gray f6">Caricamento dati Moda...</div>
                  ) : (
                    <div className="flex flex-wrap">
                      <div className="w-100 w-50-l pr0 pr3-l mb3 mb0-l">
                        <div className="f7 b mb2">Dati BS</div>
                        <div className="overflow-auto">
                          <table className="collapse w-100 f7">
                            <tbody>
                              {modaBsFields.length ? (
                                modaBsFields.map((field, index) => (
                                  <tr key={`moda-bs-${index}`}>
                                    <th className="tl pa2 bb bordoColAlt sfondoBiancoTema">
                                      {field.label}
                                    </th>
                                    <td className="pa2 bb bordoColAlt">
                                      {field.value || "-"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    className="pa2 bb bordoColAlt mid-gray"
                                    colSpan={2}
                                  >
                                    Nessun dato BS disponibile.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="w-100 w-50-l">
                        <div className="f7 b mb2">Dati VS matchati</div>
                        {modaVsMatches.length ? (
                          modaVsMatches.map((match, index) => (
                            <div
                              key={`moda-vs-${index}`}
                              className={index < modaVsMatches.length - 1 ? "mb3" : ""}
                            >
                              <div className="f7 b mb2">{match.title}</div>
                              <div className="overflow-auto">
                                <table className="collapse w-100 f7">
                                  <tbody>
                                    {match.fields.map((field, fieldIndex) => (
                                      <tr key={`moda-vs-field-${index}-${fieldIndex}`}>
                                        <th className="tl pa2 bb bordoColAlt sfondoBiancoTema">
                                          {field.label}
                                        </th>
                                        <td className="pa2 bb bordoColAlt">
                                          {field.value || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="mid-gray f7">
                            Nessun codice VS matchato.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="mb3">
                <div className="f6 b mb2">Taglie articolo (BS)</div>
                {variantRows.length ? (
                  <div className="overflow-auto">
                    <table className="collapse w-100 f7">
                      <thead>
                        <tr>
                          <th className="tl pa2 bb bordoColAlt">Ord.</th>
                          <th className="tl pa2 bb bordoColAlt">BS Taglia</th>
                          <th className="tl pa2 bb bordoColAlt">Stato</th>
                          <th className="tl pa2 bb bordoColAlt">
                            VS Articolo matchato (codVS / colore / cod prod)
                          </th>
                          <th className="tl pa2 bb bordoColAlt">VS Ordinale</th>
                          <th className="tl pa2 bb bordoColAlt">
                            VS Taglia Match
                          </th>

                          <th className="tl pa2 bb bordoColAlt">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variantRows.map((row) => {
                          const isSelected =
                            Number(row.ordinaleTagliaBottero) ===
                            Number(selectedTagliaRow?.ordinaleTagliaBottero);
                          const hasMatchedVsArtVar =
                            row.codiceArticoloVsMatched !== null &&
                            row.numeroVarianteVigliettiMatched !== null;
                          const hasMatchedVsTaglia =
                            row.ordinaleTagliaVigliettiMatched !== null;
                          const isSegnalataSenzaTaglia =
                            Number(row.articoloDaRifare) === 1 ||
                            Number(row.tagliaDeveEssereAggiuntaInImport) === 1;
                          const isHandledRow =
                            hasMatchedVsTaglia ||
                            Number(row.vaIgnorato) === 1 ||
                            Number(row.andareInCreazione) === 1 ||
                            isSegnalataSenzaTaglia;
                          let stato = "Da gestire";
                          if (
                            Number(row.tagliaDeveEssereAggiuntaInImport) === 1
                          ) {
                            stato = "Taglia da aggiungere in import";
                          } else if (Number(row.articoloDaRifare) === 1) {
                            stato = "Articolo da rifare";
                          } else if (Number(row.vaIgnorato) === 1) {
                            stato =
                              Number(row.bsInMovimenti) === 0
                                ? "Ignorata (fuori movimenti)"
                                : "Ignorata";
                          } else if (Number(row.andareInCreazione) === 1) {
                            stato = "In creazione";
                          } else if (row.ordinaleTagliaVigliettiMatched) {
                            stato = "Matchata";
                          }
                          return (
                            <tr
                              key={row.ordinaleTagliaBottero}
                              className={
                                isSelected
                                  ? "sfondoColChiaro"
                                  : isHandledRow
                                    ? "sfondoVerdeGestitoTema"
                                    : ""
                              }
                              style={{ cursor: "pointer" }}
                              onClick={() => setSelectedTagliaRow(row)}
                            >
                              <td className="pa2 bb bordoColAlt b">
                                {row.ordinaleTagliaBottero}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {row.bsTaglia || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">{stato}</td>
                              <td className="pa2 bb bordoColAlt">
                                {row.codiceArticoloVsMatched &&
                                row.numeroVarianteVigliettiMatched
                                  ? `${row.vsArticolo || "-"} / ${row.vsVariante || "-"} / ${row.vsCodProduttore || "-"}`
                                  : "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {row.ordinaleTagliaVigliettiMatched || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {row.ordinaleTagliaVigliettiMatched
                                  ? `${row.vsTaglia || "-"}`
                                  : "-"}
                              </td>

                              <td className="pa2 bb bordoColAlt">
                                {hasMatchedVsArtVar &&
                                !hasMatchedVsTaglia &&
                                !isSegnalataSenzaTaglia ? (
                                  <button
                                    type="button"
                                    className={`${themeSmallButtonClasses.primary} mr2`}
                                    onClick={async () => {
                                      setSelectedTagliaRow(row);
                                      await openSizeSelection(row, {
                                        codiceArticoloVs: Number(
                                          row.codiceArticoloVsMatched,
                                        ),
                                        numeroVarianteVs: Number(
                                          row.numeroVarianteVigliettiMatched,
                                        ),
                                        vsArticolo: row.vsArticolo || null,
                                        vsVariante: row.vsVariante || null,
                                      });
                                    }}
                                  >
                                    Seleziona taglia
                                  </button>
                                ) : null}
                                {!hasMatchedVsArtVar && !isHandledRow ? (
                                  <button
                                    type="button"
                                    className="pointer bn br2 ph2 pv1 sfondoColScuro colBianco mr2"
                                    onClick={() => selectTagliaRow(row)}
                                  >
                                    Cerca articoli per match
                                  </button>
                                ) : null}
                                {!hasMatchedVsArtVar && !isHandledRow ? (
                                  <button
                                    type="button"
                                    className={`${themeSmallButtonClasses.warning} mr2`}
                                    onClick={() => openCreatePromptForRow(row)}
                                  >
                                    Crea
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className={themeSmallButtonClasses.danger}
                                  onClick={() =>
                                    applyDecision({
                                      codiceArticoloBs: row.codiceArticoloBs,
                                      numeroVarianteBottero:
                                        row.numeroVarianteBottero,
                                      ordinaleTagliaBottero:
                                        row.ordinaleTagliaBottero,
                                      action: "clear_decision",
                                    })
                                  }
                                >
                                  Cancella il match
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="f7 dark-gray">Nessuna taglia trovata.</div>
                )}
              </div>

              {pendingMatchCandidate ? (
                <div className="flex flex-wrap mt2">
                  <div className="w-100 w-50-l pr0 pr3-l">
                    <div className="flex items-center justify-between mb2">
                      <div className="f6 b">Selezione Taglia</div>
                      <button
                        type="button"
                        className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                        onClick={async () => {
                          setPendingMatchCandidate(null);
                          setSizeSelectionContext(null);
                          setSizeOptions([]);
                          setSizeStepError("");
                          setSelectedOrdinaleTagliaVs("");
                          setShowSaveMappingPrompt(false);
                          if (selectedTagliaRow) {
                            await loadCandidates(selectedTagliaRow);
                          }
                        }}
                      >
                        Torna ai candidati
                      </button>
                    </div>

                    <div className="f7 mb2">
                      Candidato VS:{" "}
                      <b>{pendingMatchCandidate.vsArticolo || "-"}</b> /{" "}
                      <b>{pendingMatchCandidate.vsVariante || "-"}</b>
                    </div>
                    {sizeStepError ? (
                      <div className="dark-red mb2">{sizeStepError}</div>
                    ) : null}
                    {sizeOptionsLoading ? (
                      <div>Caricamento contesto taglie...</div>
                    ) : null}

                    {!sizeOptionsLoading && sizeSelectionContext ? (
                      <>
                        <div className="overflow-auto mb3">
                          <table className="collapse w-100 f7">
                            <thead>
                              <tr>
                                <th className="tl pa2 bb bordoColAlt">
                                  BS Ord.
                                </th>
                                <th className="tl pa2 bb bordoColAlt">
                                  BS Taglia
                                </th>
                                <th className="tr pa2 bb bordoColAlt">
                                  VS Ord. Match
                                </th>
                                <th className="tr pa2 bb bordoColAlt">
                                  VS Taglia Match
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sizeSelectionContext.rows.map((row) => (
                                <tr
                                  key={row.ordinaleTagliaBottero}
                                  className={
                                    Number(row.ordinaleTagliaBottero) ===
                                    Number(
                                      selectedTagliaRow?.ordinaleTagliaBottero,
                                    )
                                      ? "sfondoColAlt"
                                      : ""
                                  }
                                >
                                  <td className="pa2 bb bordoColAlt b">
                                    {row.ordinaleTagliaBottero}
                                  </td>
                                  <td className="pa2 bb bordoColAlt">
                                    {row.bsTaglia || "-"}
                                  </td>
                                  <td className="pa2 bb bordoColAlt tr">
                                    {row.ordinaleTagliaVsMatched ?? "-"}
                                  </td>
                                  <td className="pa2 bb bordoColAlt tr">
                                    {row.vsTagliaMatched || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="f6 b mb2">Taglie disponibili VS</div>
                        <div className="f7 mb2">
                          <div>
                            Gruppo taglia BS:{" "}
                            <b>
                              {sizeSelectionContext?.bs?.bsTagliaGroupSigla ||
                                "-"}
                              {" - "}
                              {sizeSelectionContext?.bs
                                ?.bsTagliaGroupDescrizione || "-"}
                            </b>
                          </div>
                          <div>
                            Gruppo taglia VS:{" "}
                            <b>
                              {sizeSelectionContext?.candidate
                                ?.vsTagliaGroupSigla || "-"}
                              {" - "}
                              {sizeSelectionContext?.candidate
                                ?.vsTagliaGroupDescrizione || "-"}
                            </b>
                          </div>
                          <div>
                            Gruppo taglia VS annullato:{" "}
                            <b>
                              {Number(
                                sizeSelectionContext?.candidate
                                  ?.vsTagliaGroupAnnullato,
                              ) === -1
                                ? "Sì"
                                : Number(
                                      sizeSelectionContext?.candidate
                                        ?.vsTagliaGroupAnnullato,
                                    ) === 0
                                  ? "No"
                                  : "n/a"}
                            </b>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-end">
                          {sizeOptions.map((item) => (
                            <button
                              key={item.ordinaleTagliaVs}
                              type="button"
                              className={`pointer bn br2 ph2 pv1 mr2 mb2 ${
                                String(item.ordinaleTagliaVs) ===
                                String(selectedOrdinaleTagliaVs)
                                  ? "tastoVerde white"
                                  : "sfondoBiancoTema scrittaDef"
                              }`}
                              onClick={() => {
                                setSelectedOrdinaleTagliaVs(
                                  String(item.ordinaleTagliaVs),
                                );
                                setShowSaveMappingPrompt(false);
                              }}
                            >
                              {item.vsTaglia || "-"}
                            </button>
                          ))}
                        </div>
                        {showSaveMappingPrompt &&
                        Number(
                          sizeSelectionContext?.bs?.bsTagliaGroupAnnullato,
                        ) !== -1 ? (
                          <div className="mt2 pa2 br2 ba bordoColAlt sfondoBiancoTema">
                            <div className="f7 mb2">
                              Vuoi salvare anche il mapping taglia in{" "}
                              <code>vs_map_taglie</code> per i prossimi
                              automatch?
                            </div>
                            <div className="flex flex-wrap">
                              <button
                                type="button"
                                className={`${themeButtonClasses.primary} mr2 mb2`}
                                onClick={() => {
                                  setShowSaveMappingPrompt(false);
                                  applyDecision({
                                    codiceArticoloBs:
                                      selectedTagliaRow.codiceArticoloBs,
                                    numeroVarianteBottero:
                                      selectedTagliaRow.numeroVarianteBottero,
                                    ordinaleTagliaBottero:
                                      selectedTagliaRow.ordinaleTagliaBottero,
                                    action: "set_match",
                                    saveMappingFromManualTaglia: true,
                                    match: {
                                      codiceArticoloVs:
                                        pendingMatchCandidate.codiceArticoloVs,
                                      numeroVarianteVs:
                                        pendingMatchCandidate.numeroVarianteVs,
                                      ordinaleTagliaVs: Number(
                                        selectedOrdinaleTagliaVs,
                                      ),
                                    },
                                  });
                                }}
                              >
                                Sì, salva mapping
                              </button>
                              <button
                                type="button"
                                className={`${themeButtonClasses.medium} mr2 mb2`}
                                onClick={() => {
                                  setShowSaveMappingPrompt(false);
                                  applyDecision({
                                    codiceArticoloBs:
                                      selectedTagliaRow.codiceArticoloBs,
                                    numeroVarianteBottero:
                                      selectedTagliaRow.numeroVarianteBottero,
                                    ordinaleTagliaBottero:
                                      selectedTagliaRow.ordinaleTagliaBottero,
                                    action: "set_match",
                                    saveMappingFromManualTaglia: false,
                                    match: {
                                      codiceArticoloVs:
                                        pendingMatchCandidate.codiceArticoloVs,
                                      numeroVarianteVs:
                                        pendingMatchCandidate.numeroVarianteVs,
                                      ordinaleTagliaVs: Number(
                                        selectedOrdinaleTagliaVs,
                                      ),
                                    },
                                  });
                                }}
                              >
                                No, solo questo match
                              </button>
                              <button
                                type="button"
                                className={`${themeButtonClasses.neutral} mb2`}
                                onClick={() => setShowSaveMappingPrompt(false)}
                              >
                                Chiudi
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt2 flex justify-end">
                            <button
                              type="button"
                              className={`${themeButtonClasses.alt} mr2`}
                              onClick={() =>
                                applyDecision({
                                  codiceArticoloBs:
                                    selectedTagliaRow.codiceArticoloBs,
                                  numeroVarianteBottero:
                                    selectedTagliaRow.numeroVarianteBottero,
                                  ordinaleTagliaBottero:
                                    selectedTagliaRow.ordinaleTagliaBottero,
                                  action: "set_ignore",
                                })
                              }
                            >
                              Ignora questa taglia
                            </button>
                            {Number(
                              sizeSelectionContext?.candidate
                                ?.vsTagliaGroupAnnullato ?? 0,
                            ) === -1 ? (
                              <>
                                <button
                                  type="button"
                                  className={`${themeButtonClasses.warning} mr2`}
                                  disabled={
                                    !pendingMatchCandidate?.codiceArticoloVs ||
                                    !pendingMatchCandidate?.numeroVarianteVs
                                  }
                                  onClick={() =>
                                    applyDecision({
                                      codiceArticoloBs:
                                        selectedTagliaRow.codiceArticoloBs,
                                      numeroVarianteBottero:
                                        selectedTagliaRow.numeroVarianteBottero,
                                      ordinaleTagliaBottero:
                                        selectedTagliaRow.ordinaleTagliaBottero,
                                      action: "set_taglia_da_aggiungere",
                                      match: {
                                        codiceArticoloVs:
                                          pendingMatchCandidate.codiceArticoloVs,
                                        numeroVarianteVs:
                                          pendingMatchCandidate.numeroVarianteVs,
                                      },
                                    })
                                  }
                                >
                                  Segnala taglia da aggiungere
                                </button>
                              </>
                            ) : null}
                            {Number(
                              sizeSelectionContext?.candidate
                                ?.vsTagliaGroupAnnullato ?? 0,
                            ) === 0 ? (
                              <button
                                type="button"
                                className={`${themeButtonClasses.danger} mr2`}
                                disabled={
                                  !pendingMatchCandidate?.codiceArticoloVs ||
                                  !pendingMatchCandidate?.numeroVarianteVs
                                }
                                onClick={() =>
                                  applyDecision({
                                    codiceArticoloBs:
                                      selectedTagliaRow.codiceArticoloBs,
                                    numeroVarianteBottero:
                                      selectedTagliaRow.numeroVarianteBottero,
                                    ordinaleTagliaBottero:
                                      selectedTagliaRow.ordinaleTagliaBottero,
                                    action: "set_articolo_da_rifare",
                                    match: {
                                      codiceArticoloVs:
                                        pendingMatchCandidate.codiceArticoloVs,
                                      numeroVarianteVs:
                                        pendingMatchCandidate.numeroVarianteVs,
                                    },
                                  })
                                }
                              >
                                Segnala articolo da rifare
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className={themeButtonClasses.primary}
                              disabled={!selectedOrdinaleTagliaVs}
                              onClick={async () => {
                                if (
                                  Number(
                                    sizeSelectionContext?.bs
                                      ?.bsTagliaGroupAnnullato,
                                  ) === -1
                                ) {
                                  applyDecision({
                                    codiceArticoloBs:
                                      selectedTagliaRow.codiceArticoloBs,
                                    numeroVarianteBottero:
                                      selectedTagliaRow.numeroVarianteBottero,
                                    ordinaleTagliaBottero:
                                      selectedTagliaRow.ordinaleTagliaBottero,
                                    action: "set_match",
                                    saveMappingFromManualTaglia: false,
                                    match: {
                                      codiceArticoloVs:
                                        pendingMatchCandidate.codiceArticoloVs,
                                      numeroVarianteVs:
                                        pendingMatchCandidate.numeroVarianteVs,
                                      ordinaleTagliaVs: Number(
                                        selectedOrdinaleTagliaVs,
                                      ),
                                    },
                                  });
                                  return;
                                }
                                try {
                                  const mappingAlreadyExists =
                                    await checkMappingAlreadyExists({
                                      codiceArticoloBs:
                                        selectedTagliaRow.codiceArticoloBs,
                                      numeroVarianteBottero:
                                        selectedTagliaRow.numeroVarianteBottero,
                                      ordinaleTagliaBottero:
                                        selectedTagliaRow.ordinaleTagliaBottero,
                                      codiceArticoloVs:
                                        pendingMatchCandidate.codiceArticoloVs,
                                      ordinaleTagliaVs: Number(
                                        selectedOrdinaleTagliaVs,
                                      ),
                                    });
                                  if (mappingAlreadyExists) {
                                    applyDecision({
                                      codiceArticoloBs:
                                        selectedTagliaRow.codiceArticoloBs,
                                      numeroVarianteBottero:
                                        selectedTagliaRow.numeroVarianteBottero,
                                      ordinaleTagliaBottero:
                                        selectedTagliaRow.ordinaleTagliaBottero,
                                      action: "set_match",
                                      saveMappingFromManualTaglia: false,
                                      match: {
                                        codiceArticoloVs:
                                          pendingMatchCandidate.codiceArticoloVs,
                                        numeroVarianteVs:
                                          pendingMatchCandidate.numeroVarianteVs,
                                        ordinaleTagliaVs: Number(
                                          selectedOrdinaleTagliaVs,
                                        ),
                                      },
                                    });
                                    return;
                                  }
                                  setShowSaveMappingPrompt(true);
                                } catch (err) {
                                  setSizeStepError(
                                    err?.response?.data?.error ??
                                      err?.message ??
                                      "Errore controllo mapping taglia",
                                  );
                                }
                              }}
                            >
                              Conferma Match
                            </button>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>

                  <div className="w-100 w-50-l mt3 mt0-l pl0 pl3-l">
                    {!sizeOptionsLoading && sizeSelectionContext ? (
                      <div className="pa2 br2 ba bordoColAlt">
                        <div className="f6 b mb2">
                          Altri prodotti dello stesso brand e persona hanno
                          matchato cosi:
                        </div>
                        {Array.isArray(sizeSelectionContext.referenceMatches) &&
                        sizeSelectionContext.referenceMatches.length ? (
                          <div className="overflow-auto">
                            <table className="collapse w-100 f7">
                              <thead>
                                <tr>
                                  <th className="tl pa2 bb bordoColAlt">
                                    Gruppo taglia VS
                                  </th>
                                  <th className="tl pa2 bb bordoColAlt">
                                    VS Taglia
                                  </th>
                                  <th className="tr pa2 bb bordoColAlt">
                                    Articoli
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sizeSelectionContext.referenceMatches.map(
                                  (item, idx) => (
                                    <tr key={`${item.vsTaglia}-${idx}`}>
                                      <td className="pa2 bb bordoColAlt">
                                        {item.vsTagliaGroup || "-"}
                                      </td>
                                      <td className="pa2 bb bordoColAlt">
                                        {item.vsTaglia || "-"}
                                      </td>
                                      <td className="pa2 bb bordoColAlt tr">
                                        {Number(item.articoliCount || 0)}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="f7 dark-gray">
                            Nessun riferimento storico disponibile.
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  {selectedTagliaRow ? (
                    <div className="sfondoColAltScuro pa3 pb2">
                      <div className="f6 b mb2 ">
                        Articoli VS candidati per il match
                      </div>
                      <form
                        className="mb2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          loadCandidates(selectedTagliaRow);
                        }}
                      >
                        <div className="flex flex-wrap items-end">
                          <div className="mr2 mb2">
                            <label className="f7 db mb1">VS Nome</label>
                            <input
                              className="input-reset ba bordoColAlt br2 pa2"
                              value={candidateSearch.vsNome}
                              onChange={(e) =>
                                setCandidateSearch((prev) => ({
                                  ...prev,
                                  vsNome: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="mr2 mb2">
                            <label className="f7 db mb1">VS Variante</label>
                            <input
                              className="input-reset ba bordoColAlt br2 pa2"
                              value={candidateSearch.vsVariante}
                              onChange={(e) =>
                                setCandidateSearch((prev) => ({
                                  ...prev,
                                  vsVariante: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="mr2 mb2">
                            <label className="f7 db mb1">
                              VS Cod produttore
                            </label>
                            <input
                              className="input-reset ba bordoColAlt br2 pa2"
                              value={candidateSearch.vsCodProduttore}
                              onChange={(e) =>
                                setCandidateSearch((prev) => ({
                                  ...prev,
                                  vsCodProduttore: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="mr2 mb2">
                            <label className="f7 db mb1">VS Cod modello</label>
                            <input
                              className="input-reset ba bordoColAlt br2 pa2"
                              value={candidateSearch.vsCodModello}
                              onChange={(e) =>
                                setCandidateSearch((prev) => ({
                                  ...prev,
                                  vsCodModello: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="mr2 mb2">
                            <label className="f7 db mb1">VS Brand</label>
                            <input
                              className="input-reset ba bordoColAlt br2 pa2"
                              value={candidateSearch.vsBrand}
                              onChange={(e) =>
                                setCandidateSearch((prev) => ({
                                  ...prev,
                                  vsBrand: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <button
                            type="submit"
                            className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2 mb2"
                          >
                            Cerca alternative
                          </button>
                          <button
                            type="button"
                            className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
                            onClick={() => {
                              setCandidateSearch({
                                vsNome: "",
                                vsVariante: "",
                                vsCodProduttore: "",
                                vsCodModello: "",
                                vsBrand: "",
                              });
                              loadCandidates(selectedTagliaRow);
                            }}
                          >
                            Reset ricerca
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : null}
                  {candidatesError ? (
                    <div className="dark-red mb2">{candidatesError}</div>
                  ) : null}
                  {candidatesLoading ? (
                    <MatchBotteroCandidatesTableSkeleton />
                  ) : selectedTagliaRow ? (
                    <div className="overflow-auto">
                      <div className="mb2 f7">
                        Kit BS (gruppo8):{" "}
                        <b>
                          {selectedTagliaRow?.bsTabGruppo8 ||
                            activeRow?.bsTabGruppo8 ||
                            "-"}
                        </b>
                      </div>
                      <table className="collapse w-100 f7">
                        <thead>
                          <tr>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              (Valori BS):
                            </th>
                            <th className="tl pa2 bb bordoColAlt"></th>
                            <th className="tl pa2 bb bordoColAlt"></th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsNome ||
                                activeRow?.bsNome ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsVariante ||
                                activeRow?.bsVariante ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsBrandConvertito ||
                                activeRow?.bsBrandConvertito ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsCodColore ||
                                activeRow?.bsCodColore ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsArticolo ||
                                activeRow?.bsArticolo ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt scrittaColAltScuro">
                              {selectedTagliaRow?.bsCodModello ||
                                activeRow?.bsCodModello ||
                                "-"}
                            </th>
                            <th className="tl pa2 bb bordoColAlt"></th>
                          </tr>
                          <tr>
                            <th className="tl pa2 bb bordoColAlt">Score</th>
                            <th className="tl pa2 bb bordoColAlt">Ragioni</th>
                            <th className="tl pa2 bb bordoColAlt">
                              VS Articolo
                            </th>
                            <th className="tl pa2 bb bordoColAlt">VS Nome</th>
                            <th className="tl pa2 bb bordoColAlt">
                              VS Variante
                            </th>
                            <th className="tl pa2 bb bordoColAlt">VS Brand</th>
                            <th className="tl pa2 bb bordoColAlt">
                              VS Cod colore
                            </th>
                            <th className="tl pa2 bb bordoColAlt">
                              VS Cod produttore
                            </th>
                            <th className="tl pa2 bb bordoColAlt">
                              VS Cod modello
                            </th>
                            <th className="tl pa2 bb bordoColAlt">Azione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.map((cand) => (
                            <tr
                              key={`${cand.codiceArticoloVs}-${cand.numeroVarianteVs}-${cand.ordinaleTagliaVs}`}
                            >
                              <td className="pa2 bb bordoColAlt b">
                                {cand.score}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.reasons.join(", ") || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsArticolo || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsNome || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsVariante || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsBrand || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsCodColore || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsCodProduttore || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                {cand.vsCodModello || "-"}
                              </td>
                              <td className="pa2 bb bordoColAlt">
                                <button
                                  type="button"
                                  className={themeSmallButtonClasses.primary}
                                  onClick={() => startMatchSelection(cand)}
                                >
                                  Scegli Taglia
                                </button>
                              </td>
                            </tr>
                          ))}
                          {!candidates.length ? (
                            <tr>
                              <td colSpan={10} className="tc scrittaColMedio">
                                <div className="pa4 mt3 f4">
                                  Nessun candidato trovato
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </>
              )}

              {showCreatePrompt ? (
                <div className="mt3 pa3 ba br2 bordoColAlt sfondoBiancoTema">
                  <div className="f6 b mb2">
                    {createPromptMode === "bulk"
                      ? "In Creazione (tutte): dati nuovo articolo VS"
                      : "Crea: dati nuovo articolo VS"}
                  </div>
                  <div className="f7 dark-gray mb3">
                    Verifica i dati proposti da BS prima di confermare.
                  </div>
                  <div className="flex flex-wrap">
                    <div className="w-100 w-25-l pr0 pr2-l mb2">
                      <label className="f7 db mb1">Brand VS (sigla)</label>
                      <input
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        maxLength={10}
                        value={createForm.nuovoBrandSeCreazione}
                        onChange={(e) =>
                          setCreateForm((prev) => ({
                            ...prev,
                            nuovoBrandSeCreazione: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="w-100 w-25-l pr0 pr2-l mb2">
                      <label className="f7 db mb1">Codice produttore</label>
                      <input
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        maxLength={40}
                        value={createForm.nuovoCodProdSeCreazione}
                        onChange={(e) =>
                          setCreateForm((prev) => ({
                            ...prev,
                            nuovoCodProdSeCreazione: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className={`${themeSmallButtonClasses.neutral} mt2`}
                        onClick={appendColorConsonantsToCodProd}
                        disabled={
                          !String(
                            createForm.nuovoCodProdSeCreazione || "",
                          ).trim() ||
                          !buildColorConsonantsSuffix(
                            createPromptSourceRow?.bsVariante ||
                              selectedTagliaRow?.bsVariante ||
                              activeRow?.bsVariante ||
                              "",
                          )
                        }
                      >
                        Aggiungi consonanti colore
                      </button>
                    </div>
                    <div className="w-100 w-25-l pr0 pr2-l mb2">
                      <label className="f7 db mb1">Codice modello</label>
                      <input
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        maxLength={40}
                        value={createForm.nuovoCodModSeCreazione}
                        onChange={(e) =>
                          setCreateForm((prev) => ({
                            ...prev,
                            nuovoCodModSeCreazione: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="w-100 w-25-l mb2">
                      <label className="f7 db mb1">Codice colore</label>
                      <input
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        maxLength={10}
                        value={createForm.nuovoCodColSeCreazione}
                        onChange={(e) =>
                          setCreateForm((prev) => ({
                            ...prev,
                            nuovoCodColSeCreazione: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  {createPromptError ? (
                    <div className="dark-red f6 mb2">{createPromptError}</div>
                  ) : null}
                  <div className="flex items-center">
                    <button
                      type="button"
                      className={`${themeButtonClasses.warning} mr2`}
                      disabled={createPromptLoading}
                      onClick={applyCreateDecision}
                    >
                      Conferma creazione
                    </button>
                    <button
                      type="button"
                      className={themeButtonClasses.neutral}
                      disabled={createPromptLoading}
                      onClick={cancelCreatePrompt}
                    >
                      Annulla
                    </button>
                    {createPromptLoading ? (
                      <span className="ml2 f7 dark-gray">Salvataggio...</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {showTagManagerModal ? (
                <div className="mt3 pa3 ba br2 bordoColAlt sfondoBiancoTema">
                  <div className="f6 b mb2">Tag da aggiungere all'articolo</div>
                  <div className="mb3">
                    {currentVariantTagLabels.length ? (
                      currentVariantTags.map((tagCode, index) => (
                        <span
                          key={`current-tag-${tagCode}`}
                          className="dib br-pill sfondoColChiaro scrittaDef ph2 pv1 mr2 mb2"
                        >
                          {currentVariantTagLabels[index] || tagCode}
                          <button
                            type="button"
                            className="pointer bn bg-transparent ml2 dark-red"
                            disabled={tagManagerLoading}
                            onClick={() => removeTagFromActiveVariant(tagCode)}
                          >
                            x
                          </button>
                        </span>
                      ))
                    ) : (
                      <div className="f7 dark-gray">Nessun tag assegnato.</div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end">
                    <div className="w-100 w-auto-l mr0 mr3-l mb2">
                      <label className="f7 db mb1">Aggiungi tag</label>
                      <select
                        className="input-reset ba bordoColAlt br2 pa2"
                        value={tagManagerSelectedCode}
                        disabled={tagManagerLoading || tagOptionsLoading}
                        onChange={(e) => setTagManagerSelectedCode(e.target.value)}
                      >
                        <option value="">Seleziona tag</option>
                        {tagOptions.map((option) => (
                          <option
                            key={`tag-option-${option.code}`}
                            value={option.code}
                          >
                            {formatTagOptionLabel(option)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className={`${themeButtonClasses.medium} mr2 mb2`}
                      disabled={tagManagerLoading || tagOptionsLoading}
                      onClick={addTagToActiveVariant}
                    >
                      {tagManagerLoading ? "Salvataggio..." : "Aggiungi tag"}
                    </button>
                    <button
                      type="button"
                      className={`${themeButtonClasses.neutral} mb2`}
                      disabled={tagManagerLoading}
                      onClick={() => {
                        setShowTagManagerModal(false);
                        setTagManagerError("");
                      }}
                    >
                      Chiudi gestione tag
                    </button>
                  </div>
                  {tagOptionsError ? (
                    <div className="dark-red f6 mt2">{tagOptionsError}</div>
                  ) : null}
                  {tagManagerError ? (
                    <div className="dark-red f6 mt2">{tagManagerError}</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MatchBotteroPage;
